/**
 * SinkingFundsTab (Wealth Tab) - Savings and investment tracking component
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import { 
  ChevronLeft, ChevronRight, Plus, Minus, Target, TrendingUp, 
  Eye, EyeOff, Check, X, Edit3, HelpCircle, Sparkles,
  PiggyBank, Building2, Calendar, ExternalLink, ArrowUpRight, ArrowDownLeft,
  BarChart3, CircleDollarSign, Info, Receipt, Lock
} from "lucide-react";
import { pickPhotoFromLibrary } from "../../utils/photoPicker";
import { resizeImageDataUrl } from "../../utils/imageResize";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Checkbox } from "../ui/checkbox";
import { formatCurrency, formatCurrencyWhole, getCurrentCurrency } from "../../utils/formatters";
import { getFundBalance as fundBalance, getProjectedAmount as fundProjected, getFundTint as fundTint, fundTintSlots } from "../../utils/fundMath"; // reorder_01
import { countsInNetWorth } from "../../utils/netWorthMath";
import { getMonthKey } from "../../utils/dateHelpers";
import { getAccentColors } from "../../utils/themes";
import { THEME_COLORS } from "../../utils/constants";
import { SavingsTrendChart, InvestmentsTrendChart } from "../charts/TrendCharts";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { PremiumFeature } from "../premium/PremiumComponents";
import WealthHero from "../wealth/WealthHero";
import { summarize as summarizePlans, todayLocalKey } from "../../utils/plansUtils";
import NetWorthTab from "./NetWorthTab";
import { usePurple } from "../../utils/purple";
import { usePlan, planFundTint, useFutureExtrasShown } from "../../utils/plan"; // plan_09, w2_12
import { FundsPath, InvestPath, investEmoji } from "../wealth/WealthPaths"; // plan_22, plan_26
import { investmentBalance, latestManual, manualDayFor, manualUpdatedLabel, localDay, changeSinceManual } from "../../utils/investMath"; // dark_13: one sum for investment balances
import IncomeSheet from "../wealth/IncomeSheet"; // plan_26
import LookingAhead from "../wealth/LookingAhead"; // plan_28
import { useSwipeBack } from "../../utils/swipeBack"; // w2_21
import { countsInProjection } from "../../utils/futureIncome"; // plan_25
import PlanPath from "../common/PlanPath"; // plan_11

/**
 * Full-width photo banner for a sinking fund.
 * Landscape photos fill the banner edge to edge, with the banner itself
 * taking the photo's own shape (clamped) so almost nothing is cropped.
 * Square and portrait photos are shown whole instead, with the space
 * either side filled by a colour averaged from the edges of the image.
 */
const FundPhotoBanner = ({ src, fallback, alt }) => {
  const [bg, setBg] = useState(fallback);
  const [aspect, setAspect] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBg(fallback);
    setAspect(null);
    if (!src) return undefined;

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;

      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspect(img.naturalWidth / img.naturalHeight);
      }

      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const pixels = ctx.getImageData(0, 0, size, size).data;

        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let y = 0; y < size; y += 1) {
          for (let x = 0; x < size; x += 1) {
            const isEdge = x < 2 || y < 2 || x > size - 3 || y > size - 3;
            if (!isEdge) continue;
            const i = (y * size + x) * 4;
            r += pixels[i];
            g += pixels[i + 1];
            b += pixels[i + 2];
            n += 1;
          }
        }
        if (!n || cancelled) return;

        // Pull the average off pure white or black so the photo still
        // reads as sitting on something rather than bleeding into it.
        const soften = (c) => Math.round((c / n) * 0.82 + 24);
        setBg('rgb(' + soften(r) + ', ' + soften(g) + ', ' + soften(b) + ')');
      } catch (e) {
        // keep the fallback tint
      }
    };
    img.src = src;

    return () => { cancelled = true; };
  }, [src, fallback]);

  // Anything meaningfully wider than it is tall fills the banner. Squares
  // and portraits keep their whole frame and get colour down the sides.
  const isWide = aspect !== null && aspect >= 1.25;
  const bannerAspect = isWide ? Math.min(Math.max(aspect, 1.38), 2.1) : null;

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden flex items-center justify-center"
      style={isWide
        ? { aspectRatio: String(bannerAspect), backgroundColor: bg }
        : { height: '230px', backgroundColor: bg }}
    >
      <img
        src={src}
        alt={alt}
        className={isWide ? 'w-full h-full object-cover' : 'h-full max-w-full object-contain'}
      />
    </div>
  );
};

const SinkingFundsTab = ({ sinkingFunds = [], extraPayments = [], transactions = [], investmentAccounts = [], userAssets = [], userDebts = [], budgetSettings = {}, saveUserData, theme = 'pink', plans = null, contributionsEnabled = false, onOpenPlanner, previousNetWorth = null, userData = null }) => {
  const { hasAccess, showFeaturePaywall, getLimit, loading: subLoading } = useSubscription();
  const canUseFutureIncome = hasAccess('futureIncome');
  const canViewHistory = hasAccess('netWorthHistory');

  // Get limits for free vs premium users
  const sinkingFundsLimit = getLimit('sinkingFunds');
  const investmentAccountsLimit = getLimit('investmentAccounts');

  const [viewMonth, setViewMonth] = useState(new Date());
  const [activeView, setActiveView] = useState(() => {
    // Set when coming back from the Contributions Planner. Read once, then cleared.
    try {
      const v = localStorage.getItem('worthly_wealth_view');
      if (v) { localStorage.removeItem('worthly_wealth_view'); return v; }
    } catch (e) { /* private mode */ }
    return 'networth';
  }); // 'networth' | 'savings' | 'investments' | 'planned'
  const [editingFund, setEditingFund] = useState(null);
  const [goalInput, setGoalInput] = useState('');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({ description: '', expected_date: '', fund_allocations: {}, investment_allocations: {} });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [incomeEdit, setIncomeEdit] = useState(null); // plan_26: the entry open in the sheet
  const [noteView, setNoteView] = useState(null); // plan_30: the entry whose note is open
  const [incomeFilter, setIncomeFilter] = useState({ kind: 'all', year: 'all' }); // plan_30: list filters
  const [editPayment, setEditPayment] = useState({ description: '', expected_date: '', fund_allocations: {}, investment_allocations: {} });
  const [editingInvestmentBalance, setEditingInvestmentBalance] = useState(null);
  const [overrideBalanceInput, setOverrideBalanceInput] = useState('');
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [investmentGoalInput, setInvestmentGoalInput] = useState('');
  const [detailFundId, setDetailFundId] = useState(null);
  const [detailInvestmentId, setDetailInvestmentId] = useState(null);
  const [showPlannerInfo, setShowPlannerInfo] = useState(false);
  const [showFundsInfo, setShowFundsInfo] = useState(false);
  const [showInvestInfo, setShowInvestInfo] = useState(false);
  const [showNetWorthInfo, setShowNetWorthInfo] = useState(false);
  const [reordering, setReordering] = useState(false); // reorder_01
  const [reorderingInv, setReorderingInv] = useState(false); // dark_05

  // Get theme-aware accent colors
  const accentColors = getAccentColors(theme);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only, on top of Pink): Pink's hot pink becomes one solid
  // purple and the detail pages are white.
  const purple = usePurple();
  const HOT = purple ? '#AC8DF2' : '#E91E8C';
  const plan = usePlan(); // plan_09
  // w2_21: swipe back from a fund or investment page, like the plan pages
  const fundPageRef = useRef(null);
  const invPageRef = useRef(null);
  useSwipeBack(() => fundPageRef.current, () => setDetailFundId(null), plan && detailFundId != null);
  useSwipeBack(() => invPageRef.current, () => { setDetailInvestmentId(null); setEditingInvestmentBalance(null); setOverrideBalanceInput(''); }, plan && detailInvestmentId != null);
  const futureExtrasShown = useFutureExtrasShown(); // w2_12
  const isPlanned = activeView === 'planned';
  const isNetWorth = activeView === 'networth';

  const monthKey = getMonthKey(viewMonth);
  const monthName = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const currentDate = new Date();
  const isCurrentMonth = viewMonth.getMonth() === currentDate.getMonth() && viewMonth.getFullYear() === currentDate.getFullYear();

  // Interest earned this month, split by account type
  const savingsInterest = transactions
    .filter(t => t.is_interest && t.month_key === monthKey && t.interest_account_type === 'fund')
    .reduce((sum, t) => sum + t.amount, 0);
  const investmentInterest = transactions
    .filter(t => t.is_interest && t.month_key === monthKey && t.interest_account_type === 'investment')
    .reduce((sum, t) => sum + t.amount, 0);

  // Initialize funds if empty (migrate old data format if needed)
  // Apply subscription limit - free users only see first N funds
  const allFunds = sinkingFunds.length > 0 
    ? sinkingFunds.map(f => ({
        ...f,
        current_by_month: f.current_by_month || (f.current ? { [monthKey]: f.current } : {})
      }))
    : [];
  const funds = allFunds.slice(0, sinkingFundsLimit);
  const lockedFunds = allFunds.slice(sinkingFundsLimit);

  // The fund whose detail page is open, resolved from the id so a deleted
  // or hidden fund quietly falls back to the grid instead of a blank page.
  const detailFund = detailFundId ? (funds.find(f => f.id === detailFundId) || null) : null;

  // Initialize investments - use empty array if none set
  // Apply subscription limit - free users only see first N investment accounts
  const allInvestments = investmentAccounts.length > 0 ? investmentAccounts : [];
  const investments = allInvestments.slice(0, investmentAccountsLimit);
  const lockedInvestments = allInvestments.slice(investmentAccountsLimit);

  // The investment account whose detail page is open, resolved from the id
  // so a deleted or locked account falls back to the grid.
  const detailInvestment = detailInvestmentId ? (investments.find(i => i.id === detailInvestmentId) || null) : null;
  // dark_05: an account's colour follows the account (investTintOf).

  // Get the end of the viewed month for filtering transactions
  // For current month, use today's date; for past months, use end of that month
  const getViewMonthCutoff = () => {
    if (isCurrentMonth) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now;
    }
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0, 23, 59, 59);
  };
  const viewMonthCutoff = getViewMonthCutoff();

  // Filter transactions to only include those up to the cutoff date
  const getTransactionsUpToViewMonth = (allTransactions) => {
    return allTransactions.filter(t => {
      const txDate = new Date(t.date);
      return txDate <= viewMonthCutoff;
    });
  };

  // Calculate fund balance based on transactions up to the viewed month.
  // Lives in src/utils/fundMath.js so the widget uses the identical function.
  const getFundBalance = (fundId) => fundBalance({
    fund: funds.find(f => f.id === fundId),
    transactions,
    cutoff: viewMonthCutoff,
  });

  // Get balance breakdown for a fund
  const getBalanceBreakdown = (fundId) => {
    const fund = funds.find(f => f.id === fundId);
    const startingBalance = fund?.starting_balance || 0;
    
    const relevantTransactions = getTransactionsUpToViewMonth(transactions);
    
    const deposits = relevantTransactions
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = relevantTransactions
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = relevantTransactions
      .filter(t => t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return { startingBalance, deposits, withdrawals, interest };
  };

  // Calculate investment balance
  const getInvestmentBalance = (invId) => {
    const inv = allInvestments.find(i => i.id === invId);
    // dark_13: the latest manual balance plus everything after it
    return investmentBalance(inv, transactions, monthKey, { cutoffDay: localDay(viewMonthCutoff) });
  };

  // An investment has a manual balance this month, or one carried from before (dark_13)
  const hasInvestmentOverride = (invId) => !!investManual(invId);
  const investManual = (invId) => latestManual(allInvestments.find(i => i.id === invId), monthKey);

  // Get all transactions for a sinking fund
  const getFundTransactions = (fundId) => {
    return transactions
      .filter(t => 
        (t.account_id === fundId && t.account_type === 'fund') ||
        (t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest)
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Get all transactions for an investment account
  const getInvestmentTransactions = (invId) => {
    return transactions
      .filter(t => 
        (t.account_id === invId && t.account_type === 'investment') ||
        (t.interest_account_id === invId && t.interest_account_type === 'investment' && t.is_interest)
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Get calculated investment balance (ignoring override)
  // What the balance would be without this month's manual balance: the last
  // one before it plus what's gone in since, or the plain calculated balance. (dark_14)
  const withoutThisMonth = (inv) => {
    if (!inv) return inv;
    const drop = (byMonth) => Object.fromEntries(Object.entries(byMonth || {}).filter(([k]) => k !== monthKey));
    return { ...inv, override_by_month: drop(inv.override_by_month), override_at_by_month: drop(inv.override_at_by_month) };
  };
  const getCalculatedInvestmentBalance = (invId) => investmentBalance(withoutThisMonth(allInvestments.find(i => i.id === invId)), transactions, monthKey, { cutoffDay: localDay(viewMonthCutoff) });
  const hasManualThisMonth = (invId) => ((allInvestments.find(i => i.id === invId) || {}).override_by_month || {})[monthKey] !== undefined;

  // Projected = balance + planned payments not yet moved (src/utils/fundMath.js)
  const getProjectedAmount = (fundId) => fundProjected({
    fund: funds.find(f => f.id === fundId),
    transactions,
    extraPayments,
    cutoff: viewMonthCutoff,
  });

  // Calculate projected amounts for investments
  const getProjectedInvestmentAmount = (invId) => {
    const currentAmount = getInvestmentBalance(invId);
    
    // Only include payments that haven't been moved to accounts yet
    const pendingPayments = extraPayments
      .filter(p => !p.moved_to_account && countsInProjection(p, viewMonth.getFullYear()))
      .reduce((sum, p) => {
        if (p.investment_allocations && p.investment_allocations[invId]) {
          return sum + p.investment_allocations[invId];
        }
        return sum;
      }, 0);
    return currentAmount + pendingPayments;
  };

  // Get balance breakdown for an investment
  const getInvestmentBreakdown = (invId) => {
    const inv = investments.find(i => i.id === invId);
    const startingBalance = inv?.starting_balance || 0;
    
    const deposits = transactions
      .filter(t => t.account_id === invId && t.account_type === 'investment' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = transactions
      .filter(t => t.account_id === invId && t.account_type === 'investment' && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = transactions
      .filter(t => t.interest_account_id === invId && t.interest_account_type === 'investment' && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return { startingBalance, deposits, withdrawals, interest };
  };

  // Investment totals for summary
  const totalInvestmentGoal = investments.reduce((sum, inv) => sum + (inv.goal || 0), 0);
  const totalInvestmentCurrent = investments.reduce((sum, inv) => sum + getInvestmentBalance(inv.id), 0);
  const totalInvestmentProjected = investments.reduce((sum, inv) => sum + getProjectedInvestmentAmount(inv.id), 0);

  const toggleFundSelection = (fundId) => {
    setNewPayment(prev => {
      const newAllocations = { ...prev.fund_allocations };
      if (newAllocations[fundId] !== undefined) {
        delete newAllocations[fundId];
      } else {
        newAllocations[fundId] = '';
      }
      return { ...prev, fund_allocations: newAllocations };
    });
  };

  const updateFundAllocation = (fundId, amount) => {
    setNewPayment(prev => ({
      ...prev,
      fund_allocations: { ...prev.fund_allocations, [fundId]: amount }
    }));
  };

  const toggleInvestmentSelection = (invId) => {
    setNewPayment(prev => {
      const newAllocations = { ...prev.investment_allocations };
      if (newAllocations[invId] !== undefined) {
        delete newAllocations[invId];
      } else {
        newAllocations[invId] = '';
      }
      return { ...prev, investment_allocations: newAllocations };
    });
  };

  const updateInvestmentAllocation = (invId, amount) => {
    setNewPayment(prev => ({
      ...prev,
      investment_allocations: { ...prev.investment_allocations, [invId]: amount }
    }));
  };

  const updateEditInvestmentAllocation = (invId, amount) => {
    setEditPayment(prev => ({
      ...prev,
      investment_allocations: { ...prev.investment_allocations, [invId]: amount }
    }));
  };

  // plan_33: two entries sharing an ID confuse the list (and editing one
  // would change both), so give any repeat a new ID, once.
  useEffect(() => {
    const seen = new Set();
    let dup = false;
    const fixed = (extraPayments || []).map((p, i) => {
      const k = String(p.id);
      if (!seen.has(k)) { seen.add(k); return p; }
      dup = true;
      const nid = Date.now() + i + Math.floor(Math.random() * 1000);
      seen.add(String(nid));
      return { ...p, id: nid };
    });
    if (dup) saveUserData({ extra_payments: fixed });
  }, [extraPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateMonth = (direction) => {
    setViewMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setEditingFund(null);
    setEditingInvestment(null);
  };

  const handleFundImageUpload = async (fundId) => {
    try {
      const dataUrl = await pickPhotoFromLibrary();
      if (!dataUrl) return;
      const resized = await resizeImageDataUrl(dataUrl, 1000, 0.82);
      const updatedFunds = allFunds.map( /* reorder_02: every fund, locked ones too */f => f.id === fundId ? { ...f, image: resized } : f);
      saveUserData({ sinking_funds: updatedFunds });
    } catch (e) {
      console.error('Fund image upload failed:', e);
    }
  };

  const removeFundImage = (fundId) => {
    const updatedFunds = allFunds.map( /* reorder_02: every fund, locked ones too */f => {
      if (f.id !== fundId) return f;
      const { image, ...rest } = f;
      return rest;
    });
    saveUserData({ sinking_funds: updatedFunds });
  };

  // Counted is the default, so switching back on removes the field entirely.
  const toggleFundInNetWorth = (fundId) => {
    const updatedFunds = allFunds.map( /* reorder_02: every fund, locked ones too */f => {
      if (f.id !== fundId) return f;
      if (countsInNetWorth(f)) return { ...f, exclude_from_net_worth: true };
      const { exclude_from_net_worth, ...rest } = f;
      return rest;
    });
    saveUserData({ sinking_funds: updatedFunds });
  };

  const updateFund = (fundId) => {
    const updatedFunds = allFunds.map( /* reorder_02: every fund, locked ones too */f => {
      if (f.id === fundId) {
        return { 
          ...f, 
          goal: goalInput ? parseFloat(goalInput) : f.goal
        };
      }
      return f;
    });
    saveUserData({ sinking_funds: updatedFunds });
    setEditingFund(null);
    setGoalInput('');
  };

  // Investment update functions
  const updateInvestment = (invId) => {
    const updatedInvestments = investments.map(inv => {
      if (inv.id === invId) {
        return { 
          ...inv, 
          goal: investmentGoalInput !== '' ? parseFloat(investmentGoalInput) || 0 : inv.goal
        };
      }
      return inv;
    });
    saveUserData({ investment_accounts: updatedInvestments });
    setEditingInvestment(null);
    setInvestmentGoalInput('');
  };

  // dark_13: a manual balance saves the day it was entered, so anything added
  // after it counts on top. Every account is kept, locked ones included.
  const setInvestmentOverride = (invId) => {
    if (overrideBalanceInput === '') return;
    const updatedInvestments = allInvestments.map(inv => {
      if (inv.id === invId) {
        return {
          ...inv,
          override_by_month: {
            ...inv.override_by_month,
            [monthKey]: parseFloat(String(overrideBalanceInput).replace(/,/g, '')) || 0
          },
          override_at_by_month: {
            ...(inv.override_at_by_month || {}),
            [monthKey]: manualDayFor(monthKey)
          }
        };
      }
      return inv;
    });
    saveUserData({ investment_accounts: updatedInvestments });
    setEditingInvestmentBalance(null);
    setOverrideBalanceInput('');
  };

  // "Use calculated": removes only this month's manual balance (dark_14).
  // Earlier months keep theirs, so your history stays as it was.
  const clearInvestmentOverride = (invId) => {
    const keep = (byMonth) => Object.fromEntries(Object.entries(byMonth || {}).filter(([k]) => k !== monthKey));
    const updatedInvestments = allInvestments.map(inv => (
      inv.id === invId ? { ...inv, override_by_month: keep(inv.override_by_month), override_at_by_month: keep(inv.override_at_by_month) } : inv
    ));
    saveUserData({ investment_accounts: updatedInvestments });
    customToast.success("Using calculated balance");
  };

  const addExtraPayment = () => {
    const fundAllocations = newPayment.fund_allocations || {};
    const investmentAllocations = newPayment.investment_allocations || {};
    const hasFundAllocations = Object.keys(fundAllocations).some(k => parseFloat(fundAllocations[k]) > 0);
    const hasInvestmentAllocations = Object.keys(investmentAllocations).some(k => parseFloat(investmentAllocations[k]) > 0);
    
    if (newPayment.description && (hasFundAllocations || hasInvestmentAllocations)) {
      // Convert string amounts to numbers and filter out zero/empty allocations
      const fund_allocations = {};
      const investment_allocations = {};
      let totalAmount = 0;
      
      Object.entries(fundAllocations).forEach(([fundId, amount]) => {
        const numAmount = parseFloat(amount) || 0;
        if (numAmount > 0) {
          fund_allocations[fundId] = numAmount;
          totalAmount += numAmount;
        }
      });
      
      Object.entries(investmentAllocations).forEach(([invId, amount]) => {
        const numAmount = parseFloat(amount) || 0;
        if (numAmount > 0) {
          investment_allocations[invId] = numAmount;
          totalAmount += numAmount;
        }
      });

      const payment = {
        id: Date.now(),
        description: newPayment.description,
        expected_date: newPayment.expected_date || new Date().toISOString().split('T')[0],
        amount: totalAmount,
        fund_allocations: fund_allocations,
        investment_allocations: investment_allocations,
        bonus_received: false,
        moved_to_account: false,
        updated_trackers: false
      };
      saveUserData({ extra_payments: [payment, ...extraPayments] });
      setNewPayment({ description: '', expected_date: '', fund_allocations: {} });
      setShowAddPayment(false);
    } else {
      customToast.error("Missing info", "Please add description and at least one fund allocation");
    }
  };

  const updatePaymentChecklist = (paymentId, field) => {
    const updated = extraPayments.map(p => {
      if (p.id === paymentId) {
        return { ...p, [field]: !p[field] };
      }
      return p;
    });
    saveUserData({ extra_payments: updated });
  };

  // w2_14: remove exactly the one you picked, even if two ever shared an id
  const deletePayment = (target) => {
    const list = extraPayments || [];
    let at = list.indexOf(target);
    if (at < 0) {
      const id = target && typeof target === 'object' ? target.id : target;
      at = list.findIndex((p) => String(p.id) === String(id));
    }
    if (at < 0) return;
    saveUserData({ extra_payments: list.filter((_, i) => i !== at) });
  };

  // plan_26: one sheet for adding and editing future income
  const closeIncomeSheet = () => { setShowAddPayment(false); setIncomeEdit(null); };
  const saveIncomeSheet = (data) => {
    if (incomeEdit) {
      saveUserData({ extra_payments: extraPayments.map((p) => (p.id === incomeEdit.id ? { ...p, ...data } : p)) });
    } else {
      saveUserData({ extra_payments: [{ id: Date.now(), bonus_received: false, moved_to_account: false, updated_trackers: false, ...data }, ...extraPayments] });
    }
    closeIncomeSheet();
  };
  // Moved to accounts also counts as received.
  const setPaymentDone = (paymentId, done) => saveUserData({
    extra_payments: extraPayments.map((p) => (p.id === paymentId ? { ...p, moved_to_account: done, bonus_received: done ? true : !!p.bonus_received } : p)),
  });

  const startEditPayment = (payment) => {
    // Convert fund_allocations and investment_allocations to string values for editing
    const fundAllocations = {};
    const investmentAllocations = {};
    if (payment.fund_allocations) {
      Object.entries(payment.fund_allocations).forEach(([fundId, amount]) => {
        fundAllocations[fundId] = amount.toString();
      });
    }
    if (payment.investment_allocations) {
      Object.entries(payment.investment_allocations).forEach(([invId, amount]) => {
        investmentAllocations[invId] = amount.toString();
      });
    }
    setEditPayment({
      description: payment.description,
      expected_date: payment.expected_date,
      fund_allocations: fundAllocations,
      investment_allocations: investmentAllocations
    });
    setEditingPaymentId(payment.id);
  };

  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditPayment({ description: '', expected_date: '', fund_allocations: {}, investment_allocations: {} });
  };

  const toggleEditFundAllocation = (fundId) => {
    setEditPayment(prev => {
      const newAllocations = { ...prev.fund_allocations };
      if (newAllocations[fundId] !== undefined) {
        delete newAllocations[fundId];
      } else {
        newAllocations[fundId] = '';
      }
      return { ...prev, fund_allocations: newAllocations };
    });
  };

  const toggleEditInvestmentAllocation = (invId) => {
    setEditPayment(prev => {
      const newAllocations = { ...(prev.investment_allocations || {}) };
      if (newAllocations[invId] !== undefined) {
        delete newAllocations[invId];
      } else {
        newAllocations[invId] = '';
      }
      return { ...prev, investment_allocations: newAllocations };
    });
  };

  const updateEditFundAllocation = (fundId, amount) => {
    setEditPayment(prev => ({
      ...prev,
      fund_allocations: { ...prev.fund_allocations, [fundId]: amount }
    }));
  };

  const saveEditPayment = () => {
    const fundAllocations = editPayment.fund_allocations || {};
    const investmentAllocations = editPayment.investment_allocations || {};
    const hasFundAllocations = Object.keys(fundAllocations).some(k => parseFloat(fundAllocations[k]) > 0);
    const hasInvestmentAllocations = Object.keys(investmentAllocations).some(k => parseFloat(investmentAllocations[k]) > 0);
    
    if (editPayment.description && (hasFundAllocations || hasInvestmentAllocations)) {
      const fund_allocations = {};
      const investment_allocations = {};
      let totalAmount = 0;
      
      Object.entries(fundAllocations).forEach(([fundId, amount]) => {
        const numAmount = parseFloat(amount) || 0;
        if (numAmount > 0) {
          fund_allocations[fundId] = numAmount;
          totalAmount += numAmount;
        }
      });
      
      Object.entries(investmentAllocations).forEach(([invId, amount]) => {
        const numAmount = parseFloat(amount) || 0;
        if (numAmount > 0) {
          investment_allocations[invId] = numAmount;
          totalAmount += numAmount;
        }
      });

      const updated = extraPayments.map(p => {
        if (p.id === editingPaymentId) {
          return {
            ...p,
            description: editPayment.description,
            expected_date: editPayment.expected_date || new Date().toISOString().split('T')[0],
            amount: totalAmount,
            fund_allocations: fund_allocations,
            investment_allocations: investment_allocations
          };
        }
        return p;
      });
      saveUserData({ extra_payments: updated });
      cancelEditPayment();
    } else {
      customToast.error("Missing info", "Please add description and at least one allocation");
    }
  };

  const isPaymentComplete = (payment) => !!payment.moved_to_account; // plan_26: one tick

  const totalGoal = funds.reduce((sum, f) => sum + f.goal, 0);
  const totalCurrent = funds.reduce((sum, f) => sum + getFundBalance(f.id), 0);
  const totalProjected = funds.reduce((sum, f) => sum + getProjectedAmount(f.id), 0);

  // ---- Sinking fund tile palette --------------------------------------
  // The eight-tint table now lives in src/utils/fundMath.js so the widget tiles
  // take the same colour a fund has in this list.
  // Purple: fund cards are white so they stand out on the lavender page.
  const getFundTint = (index) => {
    if (plan) return planFundTint(index); // plan_09: soft pastels
    const t = fundTint(index, purple ? 'purple' : isWarm ? 'warm' : isPink ? 'pink' : 'blue');
    return purple ? { ...t, wash: '#FFFFFF' } : t;
  };

  // reorder_01: a fund's colour follows the fund, not its place in the list.
  const fundSlots = fundTintSlots(allFunds);
  const fundTintOf = (fund) => {
    const k = allFunds.findIndex((f) => f.id === (fund && fund.id));
    return getFundTint(k >= 0 ? fundSlots[k] : 0);
  };
  // Move a fund up or down. The first move saves every fund's current colour
  // on the fund itself, so colours stay put. Locked funds stay at the end.
  const moveFund = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= funds.length) return;
    const stamped = allFunds.map((f, k) => (Number.isInteger(f.tint) ? f : { ...f, tint: fundSlots[k] }));
    const shown = stamped.slice(0, funds.length);
    const rest = stamped.slice(funds.length);
    const moved = shown[i];
    shown[i] = shown[j];
    shown[j] = moved;
    saveUserData({ sinking_funds: [...shown, ...rest] });
  };
  // dark_13: at the start of a month, a nudge to update manual balances.
  // Shows on Invest for this month only, for accounts that have had a manual
  // balance before and haven't had one yet this month. "Not now" hides it
  // until next month.
  const nowMonthKey = localDay(new Date()).slice(0, 7);
  const staleInvestments = investments.filter((inv) => latestManual(inv, nowMonthKey) && (inv.override_by_month || {})[nowMonthKey] === undefined);
  const investNudgeLast = staleInvestments.map((inv) => latestManual(inv, nowMonthKey)).sort((a, b) => ((a.at || a.month) < (b.at || b.month) ? 1 : -1))[0] || null;
  const investNudge = !(plan && activeView === 'investments' && monthKey === nowMonthKey && staleInvestments.length > 0 && (userData && userData.invest_update_dismissed) !== nowMonthKey) ? null : (
    <div style={{ background: '#FFFFFF', borderRadius: 24, padding: 16, marginTop: 12 }} data-testid="invest-update-nudge">
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: '#DFE9F9', fontSize: 18 }}>{'\u{1F4C8}'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E' }}>New month! Update your balances</span>
          <span style={{ display: 'block', fontSize: 13, color: '#8B879A', marginTop: 2, lineHeight: 1.4 }}>
            So your net worth matches what's really in your accounts.{investNudgeLast ? ' Last updated ' + manualUpdatedLabel(investNudgeLast, true) + '.' : ''}
          </span>
        </span>
      </div>
      <div className="flex" style={{ gap: 8, marginTop: 14 }}>
        <button
          type="button"
          onClick={() => {
            const first = staleInvestments[0];
            setDetailInvestmentId(first.id);
            setEditingInvestmentBalance(first.id);
            setOverrideBalanceInput(String(Math.round(getInvestmentBalance(first.id) * 100) / 100));
          }}
          style={{ flex: 1, border: 0, borderRadius: 999, background: '#1F1E24', color: '#FFFFFF', padding: '11px 0', fontSize: 14, fontWeight: 600 }}
        >Update</button>
        <button
          type="button"
          onClick={() => saveUserData({ invest_update_dismissed: nowMonthKey })}
          style={{ flex: 1, border: '1.5px solid #1F1E24', borderRadius: 999, background: 'transparent', color: '#26252E', padding: '10px 0', fontSize: 14, fontWeight: 600 }}
        >Not now</button>
      </div>
    </div>
  );

  // dark_05: investments reorder the same way, and keep their colours too.
  const invSlots = fundTintSlots(allInvestments);
  const investTintOf = (inv) => {
    const k = allInvestments.findIndex((a) => a.id === (inv && inv.id));
    return getFundTint(k >= 0 ? invSlots[k] : 0);
  };
  const moveInvestment = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= investments.length) return;
    const stamped = allInvestments.map((a, k) => (Number.isInteger(a.tint) ? a : { ...a, tint: invSlots[k] }));
    const shown = stamped.slice(0, investments.length);
    const rest = stamped.slice(investments.length);
    const moved = shown[i];
    shown[i] = shown[j];
    shown[j] = moved;
    saveUserData({ investment_accounts: [...shown, ...rest] });
  };

  // Split a formatted currency string so the cents can render smaller.
  // Handles both "1,016.25" and "1.016,25"; if there is no two-digit
  // fraction the whole string comes back untouched.
  const splitCurrencyParts = (formatted) => {
    const match = /^(.*)([.,]\d{2})$/.exec(String(formatted));
    return match ? { whole: match[1], cents: match[2] } : { whole: String(formatted), cents: '' };
  };

  // plan_09: Money plan pieces ------------------------------------------
  const planCard = { background: '#FFFFFF', borderRadius: 24, padding: 18, marginTop: 12 };
  const planH3 = { fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' };
  const planLink = { fontSize: 13, fontWeight: 600, color: '#8B879A', background: 'none', border: 0, padding: 0 };
  // plan_30: filters on the future income list
  // plan_31: read a planned date as the day you picked, in your own time zone
  const payDay = (d) => { const s = String(d || ''); return /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(s.slice(0, 10) + 'T12:00:00') : new Date(s); };
  const incomeHasOut = extraPayments.some((p) => p.kind === 'out');
  const incomeYears = Array.from(new Set(extraPayments.map((p) => (p.expected_date ? String(payDay(p.expected_date).getFullYear()) : '')).filter((y) => /^\d{4}$/.test(y)))).sort();
  const kindF = incomeHasOut ? incomeFilter.kind : 'all';
  const yearF = incomeYears.length > 1 ? incomeFilter.year : 'all';
  const shownPayments = extraPayments.filter((p) => (kindF === 'all' || (kindF === 'out') === (p.kind === 'out'))
    && (yearF === 'all' || (p.expected_date && String(payDay(p.expected_date).getFullYear()) === yearF)));
  const planNextPay = extraPayments
    .filter(p => !p.bonus_received && p.expected_date && p.kind !== 'out')
    .sort((a, b) => new Date(a.expected_date) - new Date(b.expected_date))[0] || null;
  // plan_11: Future income path - the last payday received, then the next three.
  const planPayParse = (d) => new Date(String(d).length === 10 ? d + 'T12:00:00' : d);
  const planPayEmoji = (pay) => {
    const fid = Object.keys(pay.fund_allocations || {})[0] || (pay.designated_funds || [])[0] || pay.designated_fund;
    const fund = fid ? funds.find((f) => f.id === fid) : null;
    if (fund && fund.emoji) return fund.emoji;
    if (pay.investment_allocations && Object.keys(pay.investment_allocations).length) return '\u{1F4C8}';
    return '\u{1F4B8}';
  };
  const planPayTotal = (pay) => {
    const f = pay.fund_allocations ? Object.values(pay.fund_allocations).reduce((s, v) => s + (Number(v) || 0), 0) : (pay.amount || 0);
    const i = pay.investment_allocations ? Object.values(pay.investment_allocations).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
    return f + i;
  };
  const planPaySteps = (() => {
    if (!plan) return [];
    const byDate = (a, b) => planPayParse(a.expected_date) - planPayParse(b.expected_date);
    const dated = extraPayments.filter((x) => x.expected_date && x.kind !== 'out'); // plan_28: paydays only
    const received = dated.filter((x) => x.bonus_received).sort(byDate);
    const upcoming = dated.filter((x) => !x.bonus_received).sort(byDate).slice(0, 3);
    const short = (d) => planPayParse(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const steps = [];
    if (received.length) {
      const last = received[received.length - 1];
      steps.push({ emoji: planPayEmoji(last), label: last.description || 'Payday', sub: short(last.expected_date), done: true });
    }
    upcoming.forEach((x, i) => steps.push({ emoji: planPayEmoji(x), label: x.description || 'Payday', sub: short(x.expected_date), now: i === 0 }));
    return steps;
  })();

  const planHeroChart = !plan ? null
    : activeView === 'savings' ? (
      <PremiumFeature feature="savingsTrends">
        <SavingsTrendChart sinkingFunds={funds} extraPayments={extraPayments} transactions={transactions} theme={theme} />
      </PremiumFeature>
    ) : activeView === 'investments' ? (
      <PremiumFeature feature="investmentsTrends">
        <InvestmentsTrendChart investmentAccounts={investmentAccounts} extraPayments={extraPayments} transactions={transactions} theme={theme} />
      </PremiumFeature>
    ) : null;
  // plan_22: the path on Funds and Invest, next to the graph
  const planHeroPath = !plan ? null
    : activeView === 'savings' && funds.length ? (
      <FundsPath items={funds.map((f) => ({ id: f.id, emoji: f.emoji, name: f.name, balance: getFundBalance(f.id), tint: fundTintOf(f) }))} /* w2_04 */ />
    ) : activeView === 'investments' && investments.length ? (
      <InvestPath
        items={investments.map((a) => ({ id: a.id, name: a.name, emoji: a.emoji, balance: getInvestmentBalance(a.id), tint: investTintOf(a) }))}
        onSaveEmoji={(id, e) => saveUserData({ investment_accounts: investmentAccounts.map((a) => (a.id === id ? { ...a, emoji: e } : a)) })}
      />
    ) : null;
  const planGoalRow = (o) => {
    const pct = Math.max(0, Math.min(100, o.pct || 0));
    const projPct = Math.max(0, Math.min(100, o.projPct || 0));
    return (
      <div key={o.id} onClick={o.onClick} className="flex items-center cursor-pointer" style={{ gap: 12, padding: '11px 0', borderTop: o.first ? 0 : '1px solid #EEEAF6' }} data-testid="plan-goal-row">
        {o.emoji ? (
          <span className="flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 15, background: o.tint.grad, fontSize: 20 }}>{o.emoji}</span>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex justify-between" style={{ gap: 8, fontSize: 15, fontWeight: 600, color: '#26252E' }}>
            <span className="truncate">{o.name}</span>
            <span style={{ flexShrink: 0 }}>{o.goal > 0 ? `${Math.round(pct)}%` : ''}</span>
          </div>
          <div className="truncate" style={{ fontSize: 13, color: '#8B879A', marginTop: 1 }}>
            <b style={{ color: '#26252E', fontWeight: 600 }}>{o.currentText}</b>
            {o.goal > 0 ? ` / ${formatCurrency(o.goal)}` : ' saved so far'}
            {o.sub ? ` · ${o.sub}` : ''}
          </div>
          <div style={{ position: 'relative', height: 6, borderRadius: 9, background: '#F0EDF6', marginTop: 7, overflow: 'hidden' }}>
            {projPct > pct && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${projPct}%`, borderRadius: 9, background: o.tint.bar, opacity: 0.38 }} />}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${o.current > 0 ? Math.max(pct, 2) : 0}%`, borderRadius: 9, background: o.tint.bar }} />
          </div>
          <div className="flex justify-between" style={{ gap: 8, fontSize: 12, color: '#ABA7B8', marginTop: 5 }}>
            <span>{o.goal > 0 && pct >= 100 ? 'Full! \u{1F389}' : o.goal > 0 ? `Projected ${Math.round(projPct)}%` : 'No goal yet'}</span>
            <span>{o.projText}</span>
          </div>
        </div>
      </div>
    );
  };
  const planLockedRow = (item, emoji, onClick) => (
    <div key={item.id} onClick={onClick} className="flex items-center cursor-pointer" style={{ gap: 12, padding: '11px 0', borderTop: '1px solid #EEEAF6' }}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 15, background: '#F5F2FA', fontSize: 20, opacity: 0.6 }}>{emoji}</span>
      <span className="truncate" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: '#ABA7B8' }}>{item.name}</span>
      <span className="flex items-center flex-shrink-0" style={{ gap: 5, background: '#1F1E24', color: '#FFFFFF', borderRadius: 999, padding: '5px 11px', fontSize: 12, fontWeight: 600 }}>
        <Lock className="w-3 h-3" /> Premium
      </span>
    </div>
  );
  const planDetailTop = (o) => {
    const ringC = 2 * Math.PI * 34;
    const pct = Math.max(0, Math.min(100, o.pct || 0));
    const status = o.status || (o.goal > 0 && pct >= 100 ? 'Full! \u{1F389}'
      : o.goal <= 0 ? 'No goal yet'
      : (o.projPct || 0) >= 100 ? 'On track'
      : o.current > 0 ? 'Building' : 'Just started');
    return (
      <div data-testid="plan-detail-top">
        <div className="flex items-center" style={{ gap: 16 }}>
          <div style={{ position: 'relative', width: 84, height: 84, flex: 'none' }}>
            <svg viewBox="0 0 84 84" style={{ width: 84, height: 84, transform: 'rotate(-90deg)' }} aria-hidden="true">
              <circle cx="42" cy="42" r="34" fill="none" stroke="#FFFFFF" strokeWidth="10" />
              {pct > 0 && <circle cx="42" cy="42" r="34" fill="none" stroke={o.tint.ring} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${(ringC * pct) / 100} ${ringC}`} />}
            </svg>
            <span className="flex items-center justify-center" style={{ position: 'absolute', inset: 0, fontSize: 26 }}>{o.emoji}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <span className="inline-flex items-center" style={{ gap: 5, borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600, background: '#FFFFFF', color: '#2F8756' }}>
              <span style={{ fontSize: 8 }}>{'●'}</span>{status}
            </span>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.8px', color: o.tint.ink, marginTop: 6, lineHeight: 1.15 }}>{o.amountText}</div>
            <div style={{ fontSize: 13, color: '#6C6879', marginTop: 2 }}>
              {o.goal > 0 ? `of ${formatCurrency(o.goal)} · projected ${Math.round(o.projPct || 0)}%` : 'no goal set yet'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3" style={{ gap: 8, marginTop: 16 }}>
          {o.tiles.map(([label, value]) => (
            <div key={label} style={{ background: 'rgba(255,255,255,0.82)', borderRadius: 16, padding: '10px 11px', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11.5, color: '#8B879A' }}>{label}</span>
              <span style={{ display: 'block', fontSize: String(value).length > 10 ? 11.5 : String(value).length > 8 ? 12.5 : 14.5, fontWeight: 700, color: o.tint.ink, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value /* dark_05: smaller when it has cents */}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto relative">
      {/* New Hero Card */}
      <WealthHero
        banner={investNudge /* dark_13 */}
        theme={theme}
        chart={planHeroChart}
        path={planHeroPath} // plan_22
        nextPaydayName={planNextPay ? planNextPay.description : ''}
        paySteps={planPaySteps}
        nextPaydayAmount={planNextPay ? planPayTotal(planNextPay) : 0}
        viewMonth={viewMonth}
        onPrevMonth={() => navigateMonth(-1)}
        onNextMonth={() => navigateMonth(1)}
        activeView={activeView}
        onViewChange={setActiveView}
        onInfoClick={() => {
          if (activeView === 'savings') setShowFundsInfo(true);
          else if (activeView === 'investments') setShowInvestInfo(true);
          else if (activeView === 'planned') setShowPlannerInfo(true);
          else if (activeView === 'networth') setShowNetWorthInfo(true);
        }}
        totalGoal={totalGoal}
        totalSaved={totalCurrent}
        totalProjected={totalProjected}
        totalInvestmentGoal={totalInvestmentGoal}
        totalInvested={totalInvestmentCurrent}
        totalInvestmentProjected={totalInvestmentProjected}
        savingsInterest={savingsInterest}
        investmentInterest={investmentInterest}
        canUseFutureIncome={canUseFutureIncome}
        canViewHistory={canViewHistory}
        showFeaturePaywall={showFeaturePaywall}
        totalExpectedIncome={extraPayments.filter(p => !p.moved_to_account && p.kind !== 'out').reduce((sum, p) => sum + (p.amount || 0), 0)}
        totalPaydays={extraPayments.filter(p => !p.moved_to_account && p.kind !== 'out').length}
        nextPaydayDate={
          extraPayments
            .filter(p => !p.bonus_received && p.expected_date && p.kind !== 'out')
            .sort((a, b) => new Date(a.expected_date) - new Date(b.expected_date))[0]?.expected_date || null
        }
      />

      {isNetWorth && (
        <NetWorthTab
          sinkingFunds={sinkingFunds}
          extraPayments={extraPayments}
          investmentAccounts={investmentAccounts}
          userAssets={userAssets}
          userDebts={userDebts}
          transactions={transactions}
          budgetSettings={budgetSettings}
          saveUserData={saveUserData}
          theme={theme}
          embedded
          viewMonth={viewMonth}
          onNavigateMonth={navigateMonth}
          showNetWorthInfo={showNetWorthInfo}
          onCloseNetWorthInfo={() => setShowNetWorthInfo(false)}
          onShowInfo={() => setShowNetWorthInfo(true)}
          previousNetWorth={previousNetWorth}
          userData={userData} // plan_22
        />
      )}

      <div className="px-4">

        {/* Investments Trend Chart - Only for Investments View - Premium Feature */}
        {!plan && activeView === 'investments' && !isPlanned && (
          <PremiumFeature feature="investmentsTrends" className="mb-6">
            <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>{'Investments trend'}</p>
            <InvestmentsTrendChart investmentAccounts={investmentAccounts} extraPayments={extraPayments} transactions={transactions} theme={theme} />
          </PremiumFeature>
        )}

        {/* Savings Trend Chart - Only for Savings View - Premium Feature */}
        {!plan && activeView === 'savings' && !isPlanned && (
          <PremiumFeature feature="savingsTrends" className="mb-6">
            <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>{'Savings trend'}</p>
            <SavingsTrendChart sinkingFunds={funds} extraPayments={extraPayments} transactions={transactions} theme={theme} />
          </PremiumFeature>
        )}

        {/* plan_09: Money plan - your funds as rows in one card */}
        {plan && activeView === 'savings' && (
          <div style={planCard} className="mb-8" data-testid="plan-funds-card">
            <div className="flex items-baseline justify-between" style={{ marginBottom: 4, gap: 12 }}>
              <span style={planH3}>Your funds</span>
              <span style={{ display: 'flex', gap: 16 }}>
                {funds.length > 1 && !reordering && <button type="button" onClick={() => setReordering(true)} style={planLink} data-testid="fund-reorder-open">Reorder</button>}
              </span>
            </div>
            {funds.length === 0 && lockedFunds.length === 0 && (
              <p style={{ margin: '8px 0 4px', fontSize: 13.5, color: '#8B879A' }}>No sinking funds yet.</p>
            )}
            {reordering && funds.map((fund, idx) => { /* reorder_01 */
              const tint = fundTintOf(fund);
              const arrow = (dir, label, off) => (
                <button
                  type="button"
                  aria-label={label}
                  disabled={off}
                  onClick={() => moveFund(idx, dir)}
                  style={{ width: 36, height: 36, borderRadius: 999, border: 0, background: off ? '#F7F5FA' : '#F1EDF8', color: off ? '#C9C5D3' : '#26252E', fontSize: 16, fontWeight: 600 }}
                >{dir < 0 ? '\u2191' : '\u2193'}</button>
              );
              return (
                <div key={fund.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: idx === 0 ? 0 : '1px solid #F2EFF6' }} data-testid="fund-reorder-row">
                  <span style={{ width: 40, height: 40, borderRadius: 14, background: tint.wash, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flex: 'none' }}>{fund.emoji || '\u{1F4B0}'}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: '#26252E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fund.name}</span>
                  {arrow(-1, 'Move up', idx === 0)}
                  {arrow(1, 'Move down', idx === funds.length - 1)}
                </div>
              );
            })}
            {reordering && (
              <button type="button" onClick={() => setReordering(false)} style={{ width: '100%', marginTop: 12, padding: '13px 0', borderRadius: 999, border: 0, background: '#1F1D24', color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>Done</button>
            )}
            {!reordering && funds.map((fund, idx) => {
              const current = getFundBalance(fund.id);
              const proj = getProjectedAmount(fund.id);
              return planGoalRow({
                id: fund.id,
                first: idx === 0,
                tint: fundTintOf(fund),
                emoji: fund.emoji || '\u{1F4B0}',
                name: fund.name,
                goal: fund.goal || 0,
                current,
                currentText: formatCurrency(current),
                pct: fund.goal > 0 ? (current / fund.goal) * 100 : 0,
                projPct: fund.goal > 0 ? (proj / fund.goal) * 100 : 0,
                projText: formatCurrency(proj),
                onClick: () => setDetailFundId(fund.id),
              });
            })}
            {lockedFunds.map(fund => planLockedRow(fund, fund.emoji || '\u{1F4B0}', () => showFeaturePaywall('sinkingFunds')))}
          </div>
        )}

        {/* Sinking Funds List - Only for Savings View */}
        {!plan && activeView === 'savings' && !isPlanned && (
          <div className="space-y-3 mb-8">
            {/* Section Header */}
            <div className="flex items-center justify-between px-1 mb-2">
              <p className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Your sinking funds</p>
              <div className="relative group">
                <button className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center text-[13px] font-bold">
                  i
                </button>
                <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-white text-gray-700 text-[13px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg border border-gray-200">
                  <p className="mb-2">Set starting balances for savings accounts during onboarding or in settings.</p>
                  <p className="mb-2">Balances automatically update as you track transactions in the budget tab.</p>
                  <p className="font-medium">Click the pencil icon to add your goals.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2.5">
              {funds.map((fund) => {
                const tint = fundTintOf(fund);
                const currentAmount = getFundBalance(fund.id);
                const progressPercent = fund.goal > 0 ? Math.min((currentAmount / fund.goal) * 100, 100) : 0;
                const projectedAmount = getProjectedAmount(fund.id);
                const projectedPercent = fund.goal > 0 ? Math.min((projectedAmount / fund.goal) * 100, 100) : 0;
                const amountParts = splitCurrencyParts(formatCurrency(currentAmount));

                return (
                  <div
                    key={fund.id}
                    onClick={() => setDetailFundId(fund.id)}
                    className="relative rounded-2xl p-3.5 cursor-pointer transition-all bg-white shadow-sm"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFund(fund.id);
                        setGoalInput(fund.goal.toString());
                      }}
                      className="absolute top-3 right-3 p-1"
                      style={{ color: tint.ink, opacity: 0.55 }}
                      aria-label="Edit goal"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>

                    <div
                      className="w-[33px] h-[33px] rounded-xl flex items-center justify-center text-[17px]"
                      style={{ backgroundColor: tint.wash }}
                    >
                      {fund.emoji || '\u{1F4B0}'}
                    </div>

                    <h3
                      className="text-[12.5px] truncate mt-2.5 mb-1.5"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1F2937' }}
                    >
                      {fund.name}
                    </h3>

                    <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', lineHeight: 1, color: tint.ink, fontWeight: undefined }}>
                      {amountParts.whole}<span style={{ fontSize: '12.5px' }}>{amountParts.cents}</span>
                    </div>

                    <p className="text-[11px] mt-1" style={{ color: tint.ink, opacity: 0.7 }}>
                      {progressPercent.toFixed(0)}% of {formatCurrency(fund.goal)}
                    </p>

                    <div className="relative h-[5px] rounded-full overflow-hidden mt-2.5" style={{ backgroundColor: '#EFEAEC' }}>
                      <div
                        className="absolute h-full rounded-full transition-all"
                        style={{ width: `${progressPercent}%`, backgroundColor: tint.bar || tint.ink }}
                      />
                    </div>
                    <div className="relative h-[5px] rounded-full overflow-hidden mt-[5px]" style={{ backgroundColor: '#EFEAEC' }}>
                      <div
                        className="absolute h-full rounded-full transition-all"
                        style={{ width: `${projectedPercent}%`, backgroundColor: tint.mid }}
                      />
                    </div>

                    <div className="flex items-baseline justify-between gap-1 mt-2 text-[10.5px]" style={{ color: tint.ink, opacity: 0.7 }}>
                      <span className="flex-shrink-0">Projected {projectedPercent.toFixed(0)}%</span>
                      <span className="truncate text-right" style={{ fontWeight: 500 }}>{formatCurrency(projectedAmount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Locked Funds - Blurred */}
            {lockedFunds.map(fund => (
              <div 
                key={fund.id} 
                className="relative bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer select-none"
                onClick={() => showFeaturePaywall('sinkingFunds')}
              >
                <div className="flex opacity-30 blur-[2px] pointer-events-none">
                  <div 
                    className="w-1.5 flex-shrink-0 bg-gray-300"
                  />
                  <div className="flex-1 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{fund.emoji}</span>
                        <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{fund.name}</h3>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full" />
                  </div>
                </div>
                <div
                  className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-2xl"
                  style={{ pointerEvents: 'none' }}
                >
                  <div
                    className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
                    style={{ background: isPink ? HOT : isWarm ? '#2D5A3D' : '#4361EE' }}
                  >
                    <Lock className="w-4 h-4 text-white" />
                    <span className="text-[15px] font-medium text-white">Premium</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* plan_09: Money plan - your accounts as rows in one card */}
        {plan && activeView === 'investments' && (
          <div style={planCard} data-testid="plan-accounts-card">
            <div className="flex items-baseline justify-between" style={{ marginBottom: 4, gap: 12 }}>
              <span style={planH3}>Your accounts</span>
              <span style={{ display: 'flex', gap: 16 }}>
                {investments.length > 1 && !reorderingInv && <button type="button" onClick={() => setReorderingInv(true)} style={planLink} data-testid="invest-reorder-open">Reorder</button>}
                {!reorderingInv && <button type="button" onClick={() => setShowInvestInfo(true)} style={planLink}>How it works {'›'}</button>}
              </span>
            </div>
            {reorderingInv && investments.map((inv, idx) => { /* dark_05 */
              const tint = investTintOf(inv);
              const arrow = (dir, label, off) => (
                <button
                  type="button"
                  aria-label={label}
                  disabled={off}
                  onClick={() => moveInvestment(idx, dir)}
                  style={{ width: 36, height: 36, borderRadius: 999, border: 0, background: off ? '#F7F5FA' : '#F1EDF8', color: off ? '#C9C5D3' : '#26252E', fontSize: 16, fontWeight: 600 }}
                >{dir < 0 ? '\u2191' : '\u2193'}</button>
              );
              return (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: idx === 0 ? 0 : '1px solid #F2EFF6' }} data-testid="invest-reorder-row">
                  <span style={{ width: 40, height: 40, borderRadius: 14, background: tint.wash, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flex: 'none' }}>{investEmoji(inv)}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: '#26252E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.name}</span>
                  {arrow(-1, 'Move up', idx === 0)}
                  {arrow(1, 'Move down', idx === investments.length - 1)}
                </div>
              );
            })}
            {reorderingInv && (
              <button type="button" onClick={() => setReorderingInv(false)} style={{ width: '100%', marginTop: 12, padding: '13px 0', borderRadius: 999, border: 0, background: '#1F1D24', color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>Done</button>
            )}
            {investments.length === 0 && lockedInvestments.length === 0 && (
              <p style={{ margin: '8px 0 4px', fontSize: 13.5, color: '#8B879A' }}>No investment accounts yet.</p>
            )}
            {!reorderingInv && investments.map((inv, idx) => {
              const current = getInvestmentBalance(inv.id);
              const proj = getProjectedInvestmentAmount(inv.id);
              const goal = inv.goal || 0;
              return planGoalRow({
                id: inv.id,
                first: idx === 0,
                tint: investTintOf(inv),
                emoji: null,
                name: inv.name,
                goal,
                current,
                currentText: formatCurrency(current),
                sub: hasInvestmentOverride(inv.id) ? 'manual, updated ' + manualUpdatedLabel(investManual(inv.id)) : 'calculated', // dark_13
                pct: goal > 0 ? (current / goal) * 100 : 0,
                projPct: goal > 0 ? (proj / goal) * 100 : 0,
                projText: formatCurrency(proj),
                onClick: () => setDetailInvestmentId(inv.id),
              });
            })}
            {lockedInvestments.map(inv => planLockedRow(inv, '\u{1F4C8}', () => showFeaturePaywall('investmentAccounts')))}
          </div>
        )}

        {/* Investments List - Only for Investments View */}
        {!plan && activeView === 'investments' && !isPlanned && (
          <div className="space-y-3 mb-8">
            {/* Section Header */}
            <div className="flex items-center justify-between px-1 mb-2">
              <p className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Your investment accounts</p>
              <div className="relative group">
                <button className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center text-[13px] font-bold">
                  i
                </button>
                <div className="absolute right-0 top-full mt-2 w-72 p-3 bg-white text-gray-700 text-[13px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg border border-gray-200">
                  <p className="mb-2">Set starting balances for investment accounts during onboarding or in settings.</p>
                  <p className="mb-2">Balances automatically update as you track transactions in the budget tab.</p>
                  <p className="mb-2">Since investment values can fluctuate, use the manual entry button to override the calculated balance with your actual account value.</p>
                  <p className="font-medium">Click the pencil icon to add your goals.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2.5">
              {investments.map((inv) => {
                const tint = investTintOf(inv);
                const currentBalance = getInvestmentBalance(inv.id);
                const isOverridden = hasInvestmentOverride(inv.id);
                const goal = inv.goal || 0;
                const progressPercent = goal > 0 ? Math.min((currentBalance / goal) * 100, 100) : 0;
                const projectedAmount = getProjectedInvestmentAmount(inv.id);
                const projectedPercent = goal > 0 ? Math.min((projectedAmount / goal) * 100, 100) : 0;
                const amountParts = splitCurrencyParts(formatCurrency(currentBalance));

                return (
                  <div
                    key={inv.id}
                    onClick={() => setDetailInvestmentId(inv.id)}
                    className="relative rounded-2xl p-3.5 cursor-pointer transition-all bg-white shadow-sm"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingInvestment(inv.id);
                        setInvestmentGoalInput(goal.toString());
                      }}
                      className="absolute top-3 right-3 p-1"
                      style={{ color: tint.ink, opacity: 0.55 }}
                      aria-label="Edit goal"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>

                    <h3
                      className="text-[13.5px] pr-7 mb-1"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1F2937' }}
                    >
                      {inv.name}
                    </h3>

                    <p
                      className="text-[10px] uppercase tracking-[0.05em] mb-2"
                      style={{ color: tint.ink, opacity: 0.6, fontWeight: 700 }}
                    >
                      {isOverridden ? 'Manual balance' : 'Calculated balance'}
                    </p>

                    <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', lineHeight: 1, color: tint.ink, fontWeight: undefined }}>
                      {amountParts.whole}<span style={{ fontSize: '12.5px' }}>{amountParts.cents}</span>
                    </div>

                    <p className="text-[11px] mt-1" style={{ color: tint.ink, opacity: 0.7 }}>
                      {goal > 0 ? `${progressPercent.toFixed(0)}% of ${formatCurrency(goal)}` : 'No target set'}
                    </p>

                    <div className="relative h-[5px] rounded-full overflow-hidden mt-2.5" style={{ backgroundColor: '#EFEAEC' }}>
                      <div
                        className="absolute h-full rounded-full transition-all"
                        style={{ width: `${progressPercent}%`, backgroundColor: tint.bar || tint.ink }}
                      />
                    </div>
                    <div className="relative h-[5px] rounded-full overflow-hidden mt-[5px]" style={{ backgroundColor: '#EFEAEC' }}>
                      <div
                        className="absolute h-full rounded-full transition-all"
                        style={{ width: `${projectedPercent}%`, backgroundColor: tint.mid }}
                      />
                    </div>

                    <div className="flex items-baseline justify-between gap-1 mt-2 text-[10.5px]" style={{ color: tint.ink, opacity: 0.7 }}>
                      <span className="flex-shrink-0">Projected {projectedPercent.toFixed(0)}%</span>
                      <span className="truncate text-right" style={{ fontWeight: 500 }}>{formatCurrency(projectedAmount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Locked Investment Accounts - Blurred */}
            {lockedInvestments.map(inv => (
              <div 
                key={inv.id} 
                className="relative bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer select-none"
                onClick={() => showFeaturePaywall('investmentAccounts')}
              >
                <div className="flex opacity-30 blur-[2px] pointer-events-none">
                  <div 
                    className="w-1.5 flex-shrink-0 bg-gray-300"
                  />
                  <div className="flex-1 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">📈</span>
                        <h3 className="text-gray-800 text-[15px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{inv.name}</h3>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full" />
                  </div>
                </div>
                <div
                  className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-2xl"
                  style={{ pointerEvents: 'none' }}
                >
                  <div
                    className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
                    style={{ background: isPink ? HOT : isWarm ? '#2D5A3D' : '#4361EE' }}
                  >
                    <Lock className="w-4 h-4 text-white" />
                    <span className="text-[15px] font-medium text-white">Premium</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contributions Planner - built like the investment cards above it,
            split into RRSP and TFSA so the width carries information.
            Canada-only (can be switched off), premium. */}
        {activeView === 'investments' && !isPlanned && contributionsEnabled && (() => {
          const p = plans || {};
          const accts = p.accounts || {};
          const sources = p.sources || [];
          const contributions = p.contributions || [];
          const year = new Date().getFullYear();
          const todayKey = todayLocalKey();
          const per = {};
          let due = 0;
          ['rrsp', 'tfsa'].forEach((a) => {
            const s = summarizePlans({
              account: a, taxYear: year, sources, contributions,
              target: (accts[a] || {}).target || 0,
              roomAvailable: (accts[a] || {}).roomAvailable,
              todayKey,
            });
            per[a] = s;
            (s.perSource || []).forEach((x) => { due += x.dueCount || 0; });
          });
          const confirmed = per.rrsp.confirmed + per.tfsa.confirmed;
          const target = per.rrsp.target + per.tfsa.target;
          const locked = !(subLoading || hasAccess('contributionsPlanner'));
          const eyebrowC = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
          const chipBg = isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
          const headTint = getFundTint(0);

          const column = (key, label, tint) => {
            const s = per[key];
            const pct = s.target > 0 ? Math.min(100, (s.confirmed / s.target) * 100) : 0;
            const projPct = s.target > 0 ? Math.min(100, (s.projected / s.target) * 100) : 0;
            const parts = splitCurrencyParts(formatCurrency(s.confirmed));
            return (
              <div className="min-w-0">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] m-0" style={{ color: tint.ink }}>{label}</p>
                <div className="mt-1" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', lineHeight: 1, color: tint.ink, fontWeight: undefined }}>
                  {parts.whole}<span style={{ fontSize: '12.5px' }}>{parts.cents}</span>
                </div>
                <p className="text-[11px] mt-1 mb-0 truncate" style={{ color: tint.ink, opacity: 0.7 }}>
                  {s.target > 0 ? `${pct.toFixed(0)}% of ${formatCurrency(s.target)}` : 'No target set'}
                </p>
                <div className="relative h-[5px] rounded-full overflow-hidden mt-2.5" style={{ backgroundColor: '#EFEAEC' }}>
                  <div className="absolute h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: tint.bar || tint.ink }} />
                </div>
                <div className="relative h-[5px] rounded-full overflow-hidden mt-[5px]" style={{ backgroundColor: '#EFEAEC' }}>
                  <div className="absolute h-full rounded-full" style={{ width: `${projPct}%`, backgroundColor: tint.mid }} />
                </div>
                <p className="text-[10.5px] mt-2 mb-0 truncate" style={{ color: tint.ink, opacity: 0.7 }}>
                  {s.target > 0 ? `Projected ${projPct.toFixed(0)}%` : ' '}
                </p>
              </div>
            );
          };

          // plan_09: Money plan - one calm card with an RRSP and a TFSA box
          if (plan) {
            const box = (key, label, c) => {
              const s = per[key];
              const pct = s.target > 0 ? Math.min(100, (s.confirmed / s.target) * 100) : 0;
              const projPct = s.target > 0 ? Math.min(100, (s.projected / s.target) * 100) : 0;
              return (
                <div style={{ background: '#F7F5FB', borderRadius: 18, padding: 12, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A' }}>{label}</span>
                  <span style={{ display: 'block', fontSize: 17, fontWeight: 700, letterSpacing: '-0.4px', color: c.ink, marginTop: 2 }}>{formatCurrencyWhole(s.confirmed)}</span>
                  <div style={{ position: 'relative', height: 6, borderRadius: 9, background: '#FFFFFF', marginTop: 8, overflow: 'hidden' }}>
                    {projPct > pct && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${projPct}%`, borderRadius: 9, background: c.bar, opacity: 0.38 }} />}
                    {pct > 0 && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.max(pct, 2)}%`, borderRadius: 9, background: c.bar }} />}
                  </div>
                  <span className="truncate" style={{ display: 'block', fontSize: 12, color: '#8B879A', marginTop: 6 }}>
                    {s.target > 0 ? `${pct.toFixed(0)}% · projected ${projPct.toFixed(0)}%` : 'No target set'}
                  </span>
                </div>
              );
            };
            return (
              <div
                onClick={() => (locked ? showFeaturePaywall('contributionsPlanner') : onOpenPlanner?.())}
                className="cursor-pointer mb-6"
                style={planCard}
                data-testid="wealth-planner-card"
              >
                <div className="flex items-baseline justify-between" style={{ gap: 12 }}>
                  <span style={planH3}>Contributions planner</span>
                  {locked ? (
                    <span style={{ ...planLink, color: '#7A5DD3' }}>{'\u{1F512}'} Premium</span>
                  ) : due > 0 ? (
                    <span style={{ background: '#FCF3D2', color: '#8A7A3C', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{due} to confirm</span>
                  ) : (
                    <span style={planLink}>Open {'›'}</span>
                  )}
                </div>
                <p style={{ margin: '3px 0 0', fontSize: 13.5, color: '#8B879A' }}>
                  {locked ? 'Plan your RRSP and TFSA contributions and keep track of your room.' : year + ' · ' + formatCurrencyWhole(confirmed) + (target > 0 ? ' of ' + formatCurrencyWhole(target) : '')}
                </p>
                {!locked && (
                  <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 12 }}>
                    {box('rrsp', 'RRSP', { ink: '#C04B7C', bar: '#F2AFC8' })}
                    {box('tfsa', 'TFSA', { ink: '#3C6DB5', bar: '#A9C6F0' })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div className="mb-6">
              <div className="flex items-center justify-between px-1 mb-2">
                <p className="text-[13px] font-bold uppercase tracking-[0.08em] m-0" style={{ color: eyebrowC }}><span className="wy-star">✦ </span>Contributions</p>
                {locked && (
                  <span className="rounded-full" style={{ background: '#fff', color: eyebrowC, padding: '4px 11px', fontSize: '12px', fontWeight: 700, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                    {'🔒'} Premium
                  </span>
                )}
              </div>
              <div
                onClick={() => (locked ? showFeaturePaywall('contributionsPlanner') : onOpenPlanner?.())}
                className="relative rounded-2xl p-3.5 cursor-pointer transition-all bg-white shadow-sm"
                data-testid="wealth-planner-card"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[17px] flex-shrink-0" style={{ background: chipBg }}>
                    {'📋'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[13.5px] m-0" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1F2937' }}>
                      Contributions Planner
                    </h3>
                    <p className="text-[10px] uppercase tracking-[0.05em] m-0 mt-0.5 truncate" style={{ color: headTint.ink, opacity: 0.6, fontWeight: 700 }}>
                      {locked ? 'RRSP & TFSA' : year + ' · ' + formatCurrency(confirmed) + (target > 0 ? ' of ' + formatCurrency(target) : '')}
                    </p>
                  </div>
                  {!locked && due > 0 ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: '#FDF0E2', color: '#8A5A1E' }}>
                      {due} to confirm
                    </span>
                  ) : (
                    <span className="text-xl flex-shrink-0" style={{ color: headTint.ink, opacity: 0.4 }}>{'›'}</span>
                  )}
                </div>

                {locked ? (
                  <p className="text-[12.5px] mt-2.5 mb-0" style={{ color: '#8C8789' }}>
                    Plan your RRSP and TFSA contributions and keep track of your room.
                  </p>
                ) : (
                  <div className="grid mt-3.5" style={{ gridTemplateColumns: '1fr 1px 1fr', columnGap: '14px' }}>
                    {column('rrsp', 'RRSP', getFundTint(0))}
                    <div style={{ background: '#F0EDF2' }} />
                    {column('tfsa', 'TFSA', getFundTint(1))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Payments Section - Future Income - Planned Tab only */}
        {showFundsInfo && createPortal(
          <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowFundsInfo(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>
                About Sinking Funds
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">⚙️</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Getting started</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Add your sinking funds and set starting balances during onboarding or anytime in Settings.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">🎯</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Set your goals</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Tap the pencil icon on any fund to set a savings goal amount.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">💰</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Track your balance</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Balances update automatically as you log transactions in the Budget tab. Tap the balance to see a full breakdown.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">🔮</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Projected balance</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Shows what your fund will be worth once all planned income from the Future Income Planner is added.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">✨</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Interest</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Log interest earned on your savings accounts using the interest transaction type in the Budget tab.</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowFundsInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-[15px]">
                Got it
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* Invest Info Bottom Sheet */}
        {showInvestInfo && createPortal(
          <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowInvestInfo(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>
                About Investments
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">⚙️</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Getting started</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Add your investment accounts and set starting balances during onboarding or anytime in Settings.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">📊</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Track your portfolio</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Set a goal for each account and track your progress.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">✏️</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Manual entry</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Since market values fluctuate, use Manual Entry to override the calculated balance with your real account value for any given month.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">🔮</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Projected balance</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Includes any planned income from the Future Income Planner allocated to this account that hasn't been moved yet.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">✨</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Interest & returns</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Log dividends or returns as interest transactions in the Budget tab.</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowInvestInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-[15px]">
                Got it
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* Planner Info Bottom Sheet */}
        <div style={{display: isPlanned ? 'block' : 'none'}} className={plan ? 'wy-plan-future' : undefined}>
        {/* FAB – Add Income */}
        <button
          onClick={() => setShowAddPayment(true)}
          className="fixed z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 active:scale-95"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
            right: '20px',
            background: plan ? '#1F1E24' : isPink ? (purple ? '#AC8DF2' : 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)') : isWarm ? '#5a9e78' : '#6381f0',
            boxShadow: plan ? '0 6px 18px rgba(31,30,36,0.18)' : isPink ? (purple ? '0 8px 20px -10px rgba(120,90,190,0.35)' : '0 8px 20px rgba(226,123,161,0.4)') : '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Plus className="w-6 h-6 text-white" />
        </button>

        {/* Funds Info Bottom Sheet */}
        {showPlannerInfo && createPortal(
          <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowPlannerInfo(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>
                How to use the Future Income Planner
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">➕</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Log expected income</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Tap Add Income to log a bonus, tax refund, extra paycheck, or any money coming in.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">📅</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Set the date and allocate</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Enter the date you expect to receive it, then split it across your sinking funds or investment accounts.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">🔮</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Projections update automatically</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Your projected balances in the Funds, Invest, and Net Worth tabs will include these planned amounts.</p>
                  </div>
                </div>
                <div className="flex gap-3">{/* plan_28 */}
                  <span className="text-xl flex-shrink-0">➖</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Plan money going out</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Know you'll spend from a fund, like a trip? Add it and switch to Going out. It comes off your projected amounts, never your current balance, and Looking ahead shows what each fund will have by the end of the year.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xl flex-shrink-0">✅</span>
                  <div>
                    <p className="text-[15px] font-semibold text-gray-700 mb-0.5">Mark it done</p>
                    <p className="text-[13px] text-gray-500 leading-relaxed">Once the money is in your accounts, tick Moved to accounts (or Spent, for money going out). It then stops counting in projections, since your balances already include it.</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowPlannerInfo(false)}
                className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-[15px]"
              >
                Got it
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* w2_12: Looking ahead and the next-year switch only show when something
            is planned for after this year (and the developer switch is on) */}
        {(() => {
          if (!futureExtrasShown) return null;
          const yr = new Date().getFullYear();
          const later = (extraPayments || []).some((p) => !p.moved_to_account && p.expected_date && Number(String(p.expected_date).slice(0, 4)) > yr);
          if (!later) return null;
          return (
            <LookingAhead
              funds={funds.map((f) => ({ id: f.id, name: f.name, emoji: f.emoji, goal: f.goal || 0, balance: fundBalance({ fund: f, transactions }) }))}
              payments={extraPayments}
              tint={(i) => getFundTint(i)}
              look={plan ? 'calm' : isPink ? (purple ? 'purple' : 'pink') : isWarm ? 'warm' : 'blue'}
            />
          );
        })()}
        {/* plan_25: next year's income in projections */}
        {(() => {
          const on = !(budgetSettings && budgetSettings.future_income_this_year_only);
          const yr = new Date().getFullYear();
          // w2_11: only when something is planned for after this year
          const later = (extraPayments || []).some((p) => !p.moved_to_account && p.expected_date && Number(String(p.expected_date).slice(0, 4)) > yr);
          if (!later || !futureExtrasShown) return null;
          return (
            <div className="flex items-center" data-patch="plan_25" style={{ gap: 12, background: '#FFFFFF', borderRadius: plan ? 22 : 16, padding: '14px 16px', marginBottom: 12, boxShadow: plan ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#26252E' }}>Count next year's income in projections</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 2, lineHeight: 1.4 }}>
                  {on ? 'Your projections include everything you\u2019ve planned.' : `Income dated after ${yr} is left out until that year.`}
                </span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label="Count next year's income in projections"
                onClick={() => saveUserData({ 'budget_settings.future_income_this_year_only': on })}
                className="relative flex-shrink-0 rounded-full"
                style={{ width: 44, height: 26, border: 0, padding: 0, background: on ? (plan ? '#9ED9B6' : '#10B981') : '#D9D6D9', transition: 'background .15s ease' }}
              >
                <span className="absolute rounded-full bg-white" style={{ width: 22, height: 22, top: 2, left: on ? 20 : 2, transition: 'left .15s ease', boxShadow: '0 1px 3px rgba(0,0,0,.15)' }} />
              </button>
            </div>
          );
        })()}
        {/* plan_30: list filters */}
        {extraPayments.length > 0 && (() => {
          const on = plan ? '#1F1E24' : isPink ? (purple ? '#8B5CE0' : '#C23A72') : isWarm ? '#2D5A3D' : '#4361EE';
          const chip = (active, label, onClick, key) => (
            <button key={key} type="button" onClick={onClick} style={{ flexShrink: 0, border: 0, borderRadius: 999, padding: '7px 13px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', background: active ? on : '#FFFFFF', color: active ? '#FFFFFF' : '#6C6879', boxShadow: active || plan ? 'none' : '0 1px 2px rgba(0,0,0,0.05)' }}>{label}</button>
          );
          const hasOut = incomeHasOut; // plan_31: kind chips only when there's money going out
          const hasYears = incomeYears.length > 1;
          if (!hasOut && !hasYears) return null;
          return (
            <div className="flex items-center" data-patch="plan_30" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none', margin: '2px -16px 4px', padding: '0 16px 4px' }}>
              {hasOut && chip(kindF === 'all', 'All', () => setIncomeFilter((f) => ({ ...f, kind: 'all' })), 'k-all')}
              {hasOut && chip(kindF === 'in', 'Coming in', () => setIncomeFilter((f) => ({ ...f, kind: 'in' })), 'k-in')}
              {hasOut && chip(kindF === 'out', 'Going out', () => setIncomeFilter((f) => ({ ...f, kind: 'out' })), 'k-out')}
              {hasOut && hasYears && <span style={{ width: 1, height: 20, background: '#DDD8E8', flexShrink: 0, margin: '0 4px' }} />}
              {incomeYears.length > 1 && chip(incomeFilter.year === 'all', 'Any year', () => setIncomeFilter((f) => ({ ...f, year: 'all' })), 'y-all')}
              {incomeYears.length > 1 && incomeYears.map((y) => chip(incomeFilter.year === y, y, () => setIncomeFilter((f) => ({ ...f, year: y })), 'y-' + y))}
            </div>
          );
        })()}
        <div
          className="rounded-2xl overflow-hidden"
          onTouchMove={(e) => { if (document.activeElement?.tagName === 'INPUT') { document.activeElement.blur(); } }}
        >
        <PremiumFeature feature="futureIncome">
          <div>

          {/* plan_26: one sheet for adding and editing future income */}
          {(showAddPayment || incomeEdit) && (
            <IncomeSheet
              payment={incomeEdit}
              funds={funds.map((f) => ({ ...f, balance: fundBalance({ fund: f, transactions }) }))}
              investments={investments.map((a) => ({ ...a, emoji: plan ? investEmoji(a) : '\u{1F4CA}' }))}
              tint={(i) => getFundTint(i)}
              look={plan ? 'calm' : isPink ? (purple ? 'purple' : 'pink') : isWarm ? 'warm' : 'blue'}
              onClose={closeIncomeSheet}
              onDelete={incomeEdit ? () => { deletePayment(incomeEdit); closeIncomeSheet(); } : null}
              onSave={saveIncomeSheet}
            />
          )}
          {/* plan_30: reading a note */}
          {noteView && createPortal(
            <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, padding: 22 }} data-patch="plan_30">
              <div className="absolute" style={{ inset: '-60px 0', background: 'rgba(0,0,0,0.4)' }} onClick={() => setNoteView(null)} />
              <div className="relative w-full" style={{ maxWidth: 380, background: '#FFFFFF', borderRadius: plan ? 26 : 20, padding: 18, boxShadow: '0 20px 50px rgba(30,20,60,0.18)', fontFamily: plan ? undefined : "'DM Sans', sans-serif" }}>
                <div className="flex items-center" style={{ gap: 10 }}>
                  <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 14, background: plan ? '#FCF3D2' : isPink ? (purple ? '#F7F0FC' : '#FDF0F8') : isWarm ? '#EDF4EE' : '#EEF0FF', fontSize: 19 }}>{'\u{1F4DD}'}</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="truncate" style={{ display: 'block', fontSize: 16, fontWeight: 600, color: '#26252E' }}>{noteView.description}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 1 }}>
                      {noteView.expected_date ? new Date(String(noteView.expected_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' \u00b7 ' : ''}{noteView.kind === 'out' ? '\u2212' : ''}{formatCurrency(noteView.amount || 0)}
                    </span>
                  </span>
                </div>
                <p style={{ margin: '14px 0 0', fontSize: 14.5, lineHeight: 1.55, color: '#33343A', whiteSpace: 'pre-wrap' }}>{noteView.note}</p>
                <div className="flex" style={{ gap: 8, marginTop: 16 }}>
                  <button type="button" onClick={() => { const pay = noteView; setNoteView(null); setIncomeEdit(pay); }} style={{ flex: 1, border: 0, borderRadius: plan ? 999 : 12, padding: 12, fontSize: 14.5, fontWeight: 600, background: '#F4F1FB', color: '#55565C' }}>Edit</button>
                  <button type="button" onClick={() => setNoteView(null)} style={{ flex: 1, border: 0, borderRadius: plan ? 999 : 12, padding: 12, fontSize: 14.5, fontWeight: 600, color: '#FFFFFF', background: plan ? '#1F1E24' : isPink ? (purple ? '#AC8DF2' : 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)') : isWarm ? '#5a9e78' : '#6381f0' }}>Done</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Payments List - Timeline Style */}
          {shownPayments.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              <p>{extraPayments.length ? 'Nothing here with these filters.' : 'No future income planned yet'}</p>
            </div>
          ) : (
            <div key={'list-' + kindF + '-' + yearF}>{/* plan_33: a fresh list for each filter */}
              {(() => {
                const byDateAsc = (a, b) => payDay(a.expected_date) - payDay(b.expected_date); // plan_31
                const upcoming = shownPayments.filter(p => !isPaymentComplete(p)).sort(byDateAsc);
                const completed = shownPayments.filter(p => isPaymentComplete(p)).sort(byDateAsc);
                const todayDate = new Date();
                const buildGroups = (list) => {
                  const groups = {};
                  list.forEach(payment => {
                    const d = payDay(payment.expected_date);
                    const key = `${d.getFullYear()}-${d.getMonth()}`;
                    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    if (!groups[key]) groups[key] = { label, payments: [], date: d };
                    groups[key].payments.push(payment);
                  });
                  return Object.values(groups);
                };
                // w2_14: upcoming and received groups for the same month get their own keys
                const orderedGroups = [
                  ...buildGroups(upcoming).map((g) => ({ ...g, gkey: 'up-' + g.label })),
                  ...buildGroups(completed).map((g) => ({ ...g, gkey: 'done-' + g.label })),
                ];
                return orderedGroups.map((group) => {
                  const isCurrentMonth = group.date.getMonth() === todayDate.getMonth() && group.date.getFullYear() === todayDate.getFullYear();
                  const isFutureMonth = group.date > todayDate && !isCurrentMonth;
                  const totalForMonth = group.payments.reduce((sum, p) => {
                    const ft = p.fund_allocations ? Object.values(p.fund_allocations).reduce((s,v) => s+v, 0) : (p.amount || 0);
                    const it = p.investment_allocations ? Object.values(p.investment_allocations).reduce((s,v) => s+v, 0) : 0;
                    return sum + (p.kind === 'out' ? -1 : 1) * (ft + it); // plan_28
                  }, 0);
                  return (
                    <div key={group.gkey} className="mb-4">
                      <div className="flex items-center gap-2 mb-3 mt-4">
                        <span className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}>{group.label}</span>
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[13px] font-semibold theme-text-500">{formatCurrency(totalForMonth)}</span>
                      </div>
                      <div className="space-y-3 pl-2">
                        {group.payments.map((payment, pi) => {
                          const hasNewFormat = payment.fund_allocations && Object.keys(payment.fund_allocations).length > 0;
                          const hasInvestmentAllocations = payment.investment_allocations && Object.keys(payment.investment_allocations).length > 0;
                          const fundsList = hasNewFormat ? Object.keys(payment.fund_allocations) : (payment.designated_funds || (payment.designated_fund ? [payment.designated_fund] : []));
                          const investmentsList = hasInvestmentAllocations ? Object.keys(payment.investment_allocations) : [];
                          const isComplete = isPaymentComplete(payment);
                          const fundTotal = hasNewFormat ? Object.values(payment.fund_allocations).reduce((sum, amt) => sum + amt, 0) : (payment.amount || 0);
                          const investmentTotal = hasInvestmentAllocations ? Object.values(payment.investment_allocations).reduce((sum, amt) => sum + amt, 0) : 0;
                          const totalAmount = fundTotal + investmentTotal;
                          const isEditing = editingPaymentId === payment.id;
                          const payDate = payDay(payment.expected_date);
                          const dayNum = payDate.getDate();
                          const weekday = payDate.toLocaleDateString('en-US', { weekday: 'short' });
                          const dateBadge = plan
                            ? (isComplete ? { bg: '#F5F2FA', day: '#ABA7B8', weekday: '#ABA7B8' } : { bg: getFundTint(pi + 2).wash, day: getFundTint(pi + 2).ink, weekday: getFundTint(pi + 2).ink })
                            : isComplete
                            ? { bg: '#E9E8EB', day: '#A6A3AB', weekday: '#B3AFB8' }
                            : isPink
                            ? (purple ? { bg: '#F3E8FB', day: '#A633C4', weekday: '#B590CE' } : { bg: '#FBE3EF', day: '#E91E8C', weekday: '#C87BA0' })
                            : isWarm
                            ? { bg: '#F0E5D2', day: '#7C5C3A', weekday: '#B89972' }
                            : { bg: '#E4E8FB', day: '#4361EE', weekday: '#8B93D9' };
                          return (
                            <div key={String(payment.id) + '-' + pi} className="flex overflow-hidden rounded-2xl mb-2" style={plan ? { background: '#FFFFFF', borderRadius: 22, opacity: isComplete ? 0.55 : 1 } : { background: isComplete ? '#F6F5F7' : '#FFFFFF', boxShadow: '0 2px 10px rgba(0,0,0,.06)' }}>
                              <div className="flex flex-col items-center justify-center w-14 flex-shrink-0 m-2 rounded-2xl py-2" style={{ background: dateBadge.bg }}>
                                <span style={{ fontFamily: "'DM Serif Display', serif", color: dateBadge.day }} className="text-2xl leading-none font-normal">{dayNum}</span>
                                <span className="text-[8px] uppercase font-bold mt-0.5 tracking-wide" style={{ color: dateBadge.weekday }}>{weekday}</span>
                              </div>
                              <div className="flex-1 overflow-hidden">
                                {isEditing ? (
                                  <div className="p-4 space-y-3">
                                    <Input type="text" placeholder="Description" value={editPayment.description} onChange={(e) => setEditPayment(prev => ({ ...prev, description: e.target.value }))} className="theme-border-200" />
                                    <div>
                                      <label className="text-[13px] text-gray-500 mb-1 block">Expected Date</label>
                                      <input type="date" value={editPayment.expected_date} onChange={(e) => setEditPayment(prev => ({ ...prev, expected_date: e.target.value }))} className="block w-full max-w-[200px] px-3 text-[15px] border theme-border-200 rounded-md focus:outline-none bg-white" style={{height:"36px",lineHeight:"36px",paddingTop:0,paddingBottom:0}} />
                                    </div>
                                    <div>
                                      <label className="text-[13px] text-gray-500 mb-2 block">Allocate to sinking fund(s):</label>
                                      <div className="space-y-2">
                                        {funds.map(fund => {
                                          const isFundSelected = editPayment.fund_allocations?.[fund.id] !== undefined;
                                          return (
                                            <div key={fund.id} className={`p-3 rounded-lg border transition-colors ${isFundSelected ? 'theme-bg-100 theme-border-300' : 'bg-white theme-border-100'}`}>
                                              <label className="flex items-center gap-2 cursor-pointer">
                                                <Checkbox checked={isFundSelected} onCheckedChange={() => toggleEditFundAllocation(fund.id)} className="theme-border-300 data-[state=checked]:theme-bg-500" />
                                                <span className="text-[15px] flex-1">{fund.emoji} {fund.name.replace(' Fund', '')}</span>
                                              </label>
                                              {isFundSelected && (
                                                <div className="mt-2 relative">
                                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                                                  <Input type="text" inputMode="decimal" placeholder="Amount" value={editPayment.fund_allocations[fund.id] || ''} onChange={(e) => updateEditFundAllocation(fund.id, e.target.value)} className="pl-8 theme-border-200 bg-white" />
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-[13px] text-gray-500 mb-2 block">Allocate to investment(s):</label>
                                      <div className="space-y-2">
                                        {investments.map(inv => {
                                          const isInvSelected = editPayment.investment_allocations?.[inv.id] !== undefined;
                                          return (
                                            <div key={inv.id} className={`p-3 rounded-lg border transition-colors ${isInvSelected ? (isWarm ? 'bg-[#edf4ee] border-[#c8b99a]' : isPink ? 'bg-[#fdf5fe] border-[#e8c0e5]' : 'bg-[#E8F0F6] border-blue-300') : (isWarm ? 'bg-white border-[#e8e0d5]' : isPink ? 'bg-white border-[#ede8f5]' : 'bg-white border-blue-100')}`}>
                                              <label className="flex items-center gap-2 cursor-pointer">
                                                <Checkbox checked={isInvSelected} onCheckedChange={() => toggleEditInvestmentAllocation(inv.id)} className={isWarm ? 'border-[#c8b99a] data-[state=checked]:bg-[#2D5A3D]' : isPink ? 'border-[#e8c0e5] data-[state=checked]:bg-[#E91E8C]' : 'border-blue-300 data-[state=checked]:bg-[#2b6cee]'} />
                                                <span className="text-[15px] flex-1">{inv.name}</span>
                                              </label>
                                              {isInvSelected && (
                                                <div className="mt-2 relative">
                                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                                                  <Input type="text" inputMode="decimal" placeholder="Amount" value={editPayment.investment_allocations[inv.id] || ''} onChange={(e) => updateEditInvestmentAllocation(inv.id, e.target.value)} className="pl-8 border-blue-200 bg-white" />
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={saveEditPayment}
                                        className="flex-1 text-white hover:opacity-90"
                                        style={{
                                          background: plan ? '#1F1E24' : isPink ? (purple ? '#AC8DF2' : 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)') : isWarm ? '#5a9e78' : '#6381f0',
                                          boxShadow: plan || purple || !isPink ? 'none' : '0 4px 12px rgba(226,123,161,0.3)', // plan_24: no glow
                                        }}
                                      ><Check className="w-4 h-4 mr-1" /> Save</Button>
                                      <Button variant="outline" onClick={cancelEditPayment} className="flex-1">Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="p-4 pb-2">
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          <h3 className="text-gray-800 text-[15px] mb-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{payment.description}{payment.note ? (<button type="button" onClick={() => setNoteView(payment)} data-patch="plan_30" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, verticalAlign: 'middle', border: 0, borderRadius: 999, padding: '3px 8px', fontSize: 11.5, fontWeight: 600, background: plan ? '#FCF3D2' : isPink ? (purple ? '#F7F0FC' : '#FDF0F8') : isWarm ? '#EDF4EE' : '#EEF0FF', color: plan ? '#8A7A3C' : isPink ? (purple ? '#8B5CE0' : '#C23A72') : isWarm ? '#2D5A3D' : '#4361EE' }}>{'\u{1F4DD}'} Note</button>) : null}</h3>
                                          <div className="flex flex-wrap gap-1">
                                            {fundsList.map(fundId => {
                                              const fund = funds.find(f => f.id === fundId);
                                              const amount = hasNewFormat ? payment.fund_allocations[fundId] : (fundTotal / fundsList.length);
                                              return fund ? (
                                                <span key={fundId} className={`inline-flex items-center gap-1 text-[10px] border rounded-full px-2 py-0.5 ${isComplete ? 'bg-gray-100 border-gray-200 text-gray-400' : isPink ? 'bg-purple-50 border-purple-100 text-[#7B5EA7]' : 'theme-bg-50 theme-border-100 theme-text-500'}`}>
                                                  {fund.emoji} {fund.name.replace(' Fund', '')} · {formatCurrency(amount)}
                                                </span>
                                              ) : null;
                                            })}
                                            {investmentsList.map(invId => {
                                              const inv = investments.find(i => i.id === invId);
                                              const amount = payment.investment_allocations[invId];
                                              return inv ? (
                                                <span key={invId} className={`inline-flex items-center gap-1 text-[10px] border rounded-full px-2 py-0.5 ${isComplete ? 'bg-gray-100 border-gray-200 text-gray-400' : isPink ? 'bg-purple-50 border-purple-100 text-[#7B5EA7]' : isWarm ? 'bg-[#edf4ee] border-[#c8b99a] text-[#2D5A3D]' : 'bg-blue-50 border-blue-100 text-[#4B3FD8]'}`}>
                                                  📊 {inv.name} · {formatCurrency(amount)}
                                                </span>
                                              ) : null;
                                            })}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
                                          <span className={`font-bold text-base ${isComplete ? 'text-gray-400' : payment.kind === 'out' ? '' : 'theme-text-500'}`} style={payment.kind === 'out' && !isComplete ? { color: '#C04B7C' } : undefined}>{payment.kind === 'out' ? '\u2212' : ''}{formatCurrency(totalAmount)}</span>
                                          <div className="flex gap-1">
                                            <button onClick={() => setIncomeEdit(payment)} className="p-1.5 text-gray-300 hover:theme-text-500 transition-colors"><Edit3 className="w-3.5 h-3.5" /></button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex border-t border-gray-50">{/* plan_26: one tick */}
                                      <label className="flex-1 flex items-center justify-center gap-2 py-2.5 cursor-pointer" style={{ transform: 'translateX(-36px)' }}>{/* plan_30: centred on the whole card */}
                                        <Checkbox checked={!!payment.moved_to_account} onCheckedChange={() => setPaymentDone(payment.id, !payment.moved_to_account)} className="theme-border-300 data-[state=checked]:bg-gray-400" />
                                        <span className={`text-[13px] font-medium ${isComplete ? 'text-gray-400' : isPink ? 'text-[#7B5EA7]' : 'text-gray-400'}`}>{payment.kind === 'out' ? 'Spent' : 'Moved to accounts'}</span>
                                      </label>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
          </div>
        </PremiumFeature>
        </div>
        </div>
      </div>
      {/* Fund Detail Page */}
      {detailFund && (() => {
        const tint = fundTintOf(detailFund);
        const eyebrowColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
        const pageBg = plan ? '#F4F1FB' : isPink ? (purple ? '#F6F3FA' : '#FBEFF5') : isWarm ? '#FAF7F2' : '#EEF2FF';
        const goal = detailFund.goal || 0;
        const currentAmount = getFundBalance(detailFund.id);
        const projectedAmount = getProjectedAmount(detailFund.id);
        const progressPercent = goal > 0 ? Math.min((currentAmount / goal) * 100, 100) : 0;
        const projectedPercent = goal > 0 ? Math.min((projectedAmount / goal) * 100, 100) : 0;
        const amountParts = splitCurrencyParts(formatCurrency(currentAmount));
        const breakdown = getBalanceBreakdown(detailFund.id);
        const fundTransactions = getFundTransactions(detailFund.id);

        return (
          <div ref={fundPageRef} data-swipe-layer="1" className="fixed inset-0 z-[150] overflow-y-auto wy-plan-detail" style={{ backgroundColor: pageBg }}>
            <div className="max-w-4xl mx-auto px-4 pb-32" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}>

              {detailFund.image ? (
                <div className="relative">
                  <FundPhotoBanner src={detailFund.image} fallback={tint.wash} alt={detailFund.name} />
                  <button
                    onClick={() => setDetailFundId(null)}
                    className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center"
                    aria-label="Back to funds"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-800" />
                  </button>
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    <button
                      onClick={() => (handleFundImageUpload(detailFund.id))}
                      className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center"
                      aria-label="Replace photo"
                    >
                      <Edit3 className="w-4 h-4 text-gray-800" />
                    </button>
                    <button
                      onClick={() => removeFundImage(detailFund.id)}
                      className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center"
                      aria-label="Remove photo"
                    >
                      <X className="w-4 h-4 text-gray-800" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 mb-1">
                  <button
                    onClick={() => setDetailFundId(null)}
                    className="w-9 h-9 rounded-full bg-white/75 flex items-center justify-center flex-shrink-0"
                    aria-label="Back to funds"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-800" />
                  </button>
                  <h2 className="text-[15px] text-gray-800 truncate" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {detailFund.name}
                  </h2>
                </div>
              )}

              {/* Hero */}
              <div className="rounded-2xl p-4 mt-3 relative wy-hero wy-plan-dhero" style={{ backgroundColor: tint.wash }}>
                {!detailFund.image && (
                  <button
                    onClick={() => (handleFundImageUpload(detailFund.id))}
                    className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px]"
                    style={{ backgroundColor: 'rgba(255,255,255,0.8)', color: tint.ink, fontWeight: 500 }}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.2} />
                    {'Add photo'}
                  </button>
                )}

                {plan ? planDetailTop({
                  tint,
                  emoji: detailFund.emoji || '\u{1F4B0}',
                  amountText: formatCurrency(currentAmount),
                  current: currentAmount,
                  goal,
                  pct: progressPercent,
                  projPct: projectedPercent,
                  tiles: [
                    ['Progress', goal > 0 ? `${progressPercent.toFixed(0)}%` : '\u2014'],
                    ['Projected', formatCurrency(projectedAmount)],
                    ['Goal', goal > 0 ? formatCurrency(goal) : 'None yet'],
                  ],
                }) : (<>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[20px]"
                  style={{ backgroundColor: 'rgba(255,255,255,0.78)' }}
                >
                  {detailFund.emoji || '\u{1F4B0}'}
                </div>

                <h3
                  className="text-[14px] truncate mt-3 mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1F2937' }}
                >
                  {detailFund.name}
                </h3>

                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: '38px', lineHeight: 1, color: purple ? '#5E35A8' : tint.ink }}>
                  {amountParts.whole}<span style={{ fontSize: '20px' }}>{amountParts.cents}</span>
                </div>

                <p className="text-[12.5px] mt-1" style={{ color: tint.ink, opacity: 0.78 }}>
                  {progressPercent.toFixed(0)}% of {formatCurrency(goal)}
                </p>

                <div className="flex items-center gap-2.5 mt-4">
                  <span className="text-[10.5px] uppercase tracking-[0.04em] flex-shrink-0" style={{ color: tint.ink, opacity: 0.7, width: '46px', fontWeight: 700 }}>Now</span>
                  <div className="relative h-1.5 rounded-full overflow-hidden flex-1" style={{ backgroundColor: 'rgba(255,255,255,0.75)' }}>
                    <div className="absolute h-full rounded-full" style={{ width: `${progressPercent}%`, backgroundColor: tint.bar || tint.ink }} />
                  </div>
                  <span className="text-[11.5px] text-right flex-shrink-0" style={{ color: tint.ink, width: '78px', fontWeight: 500 }}>{formatCurrency(currentAmount)}</span>
                </div>
                <div className="flex items-center gap-2.5 mt-2">
                  <span className="text-[10.5px] uppercase tracking-[0.04em] flex-shrink-0" style={{ color: tint.ink, opacity: 0.55, width: '46px', fontWeight: 700 }}>Planned</span>
                  <div className="relative h-1.5 rounded-full overflow-hidden flex-1" style={{ backgroundColor: 'rgba(255,255,255,0.75)' }}>
                    <div className="absolute h-full rounded-full" style={{ width: `${projectedPercent}%`, backgroundColor: tint.mid }} />
                  </div>
                  <span className="text-[11.5px] text-right flex-shrink-0" style={{ color: tint.ink, opacity: 0.7, width: '78px', fontWeight: 500 }}>{formatCurrency(projectedAmount)}</span>
                </div>
                </>)}
              </div>

              {/* plan_11: Milestones - Started, 25%, Half, 75%, Full */}
              {plan && goal > 0 && (() => {
                const marks = [[0, 'Started', '\u{1F331}'], [25, '25%', '✨'], [50, 'Half', '⭐'], [75, '75%', '\u{1F680}'], [100, 'Full', '\u{1F389}']];
                const pctNow = goal > 0 ? (currentAmount / goal) * 100 : 0;
                let reached = 0;
                marks.forEach((m, i) => { if (pctNow >= m[0]) reached = i; });
                const next = marks[reached + 1];
                const toGo = next ? Math.max(0, (goal * next[0]) / 100 - currentAmount) : 0;
                const line = next
                  ? (reached >= 2 ? "You're past halfway!" : reached === 1 ? 'A quarter of the way there!' : 'Every deposit moves you along.')
                  : 'This fund is full!';
                return (
                  <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '20px 18px 18px', marginTop: 14 }} data-testid="plan-fund-milestones">
                    <div className="flex items-start justify-between" style={{ gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>Milestones</h3>
                        <p style={{ margin: '5px 0 0', fontSize: 13.5, color: '#7A7A82', lineHeight: 1.45 }}>{line}</p>
                      </div>
                      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 50, height: 50, borderRadius: 999, background: tint.wash, fontSize: 23 }}>{detailFund.emoji || '\u{1F4B0}'}</span>
                    </div>
                    <PlanPath
                      steps={marks.map((m, i) => ({ emoji: m[2], label: m[1], done: i < reached || (i === reached && !next), now: i === reached && !!next }))}
                      progress={reached / (marks.length - 1)}
                    />
                    <div style={{ background: '#F4F1FB', borderRadius: 20, padding: '14px 16px', marginTop: 16 }}>
                      <span style={{ display: 'block', fontSize: 12.5, color: '#9A8FBF', fontWeight: 500 }}>{next ? 'Next milestone' : 'Goal reached'}</span>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31', marginTop: 3 }}>
                        {next ? `${formatCurrency(toGo)} more to reach ${next[1] === 'Full' ? 'your goal' : next[1] === 'Half' ? 'halfway' : next[1]}` : 'Full! \u{1F389}'}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Goal */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Goal</p>
              <div className="bg-white rounded-2xl shadow-sm px-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Target</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{formatCurrency(goal)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Projected</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{projectedPercent.toFixed(0)}% {'\u00B7'} {formatCurrency(projectedAmount)}</span>
                </div>
                <button
                  onClick={() => { setEditingFund(detailFund.id); setGoalInput(goal.toString()); }}
                  className="w-full flex items-center justify-between py-3.5 text-[13.5px]"
                  style={{ color: tint.ink, fontWeight: 500 }}
                >
                  <span>Edit goal</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11.5px] text-gray-400 mt-2 px-1">Contributions are set up in the Budget tab.</p>

              {/* Net worth */}
              {(() => {
                const counted = countsInNetWorth(detailFund);
                return (
                  <>
                    <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Net worth</p>
                    <div className="bg-white rounded-2xl shadow-sm px-4">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={counted}
                        onClick={() => toggleFundInNetWorth(detailFund.id)}
                        className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
                        data-testid="fund-networth-switch"
                      >
                        <span className="text-[13.5px] text-gray-800" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                          Count in net worth
                        </span>
                        <span
                          className="relative flex-shrink-0 rounded-full transition-colors"
                          style={{ width: '44px', height: '26px', backgroundColor: counted ? (plan ? '#9ED9B6' : tint.ink) : (plan ? '#E4E0EC' : '#E5E1E4') }}
                        >
                          <span
                            className="absolute rounded-full bg-white transition-all"
                            style={{ top: '3px', left: counted ? '21px' : '3px', width: '20px', height: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.18)' }}
                          />
                        </span>
                      </button>
                    </div>
                    <p className="text-[11.5px] text-gray-400 mt-2 px-1 leading-relaxed">
                      Turning this off leaves this fund out of your net worth, both going forward and in your past net worth and charts. The fund itself doesn't change.
                    </p>
                  </>
                );
              })()}

              {/* Balance */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Balance</p>
              <div className="bg-white rounded-2xl shadow-sm px-4 py-1">
                <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Starting balance</span>
                  <span className="text-[13.5px] text-gray-700">{formatCurrency(breakdown.startingBalance)}</span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Deposits</span>
                  <span className="text-[13.5px] text-emerald-600">{formatCurrency(breakdown.deposits)}</span>
                </div>
                {breakdown.interest > 0 && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-[13.5px] text-gray-500">Interest</span>
                    <span className="text-[13.5px] text-[#5B8DB8]">{formatCurrency(breakdown.interest)}</span>
                  </div>
                )}
                {breakdown.withdrawals > 0 && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-[13.5px] text-gray-500">Withdrawals</span>
                    <span className="text-[13.5px] text-red-600">{formatCurrency(breakdown.withdrawals)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2.5">
                  <span className="text-[13.5px] text-gray-700" style={{ fontWeight: 500 }}>Current balance</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{formatCurrency(currentAmount)}</span>
                </div>
              </div>

              {/* Transactions */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Transactions</p>
              <div className="bg-white rounded-2xl shadow-sm px-4 py-1">
                {fundTransactions.length > 0 ? (
                  fundTransactions.map(t => (
                    <div key={t.id} className="py-2.5 border-b border-gray-100 last:border-0 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[14px] text-gray-800 truncate">{t.description}</p>
                        <p className="text-[12px] text-gray-400">
                          {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {t.is_interest && <span className="ml-1 text-blue-500">{'\u2022'} Interest</span>}
                        </p>
                      </div>
                      <span className={`text-[14px] font-medium flex-shrink-0 ${t.transaction_type === 'withdrawal' ? 'text-red-500' : 'text-green-600'}`}>
                        {t.transaction_type === 'withdrawal' ? '-' : '+'}{formatCurrency(t.amount)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-[13px] text-gray-400 py-4 text-center">No transactions yet.</p>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* Edit Fund Goal Overlay */}
      {editingFund !== null && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => { setEditingFund(null); setGoalInput(''); }}>
          <div className="bg-white rounded-3xl w-full max-w-xs mx-4 overflow-hidden shadow-2xl wy-plan-goalpop" onClick={e => e.stopPropagation()}>
            <div className="p-5 text-center">
              <h3 className="text-lg font-bold text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Edit Goal 🎯
              </h3>
              <p className="text-[13px] text-gray-400 mt-1">What are you saving for?</p>
            </div>
            <div className="px-5 pb-2">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="Goal amount"
                  className="w-full h-14 pl-10 pr-4 text-center text-2xl font-bold rounded-xl focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-2 p-5 pt-3">
              <button onClick={() => { setEditingFund(null); setGoalInput(''); }} className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-[15px] hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => editingFund && updateFund(editingFund)}
                className="flex-1 py-2.5 rounded-xl text-white font-semibold text-[15px]"
                style={{ background: theme === 'pink'
                  ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)'
                  : theme === 'warm'
                    ? '#5a9e78'
                    : '#6381f0' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investment Account Detail Page */}
      {detailInvestment && (() => {
        const inv = detailInvestment;
        const tint = investTintOf(inv); // dark_05
        const eyebrowColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
        const pageBg = plan ? '#F4F1FB' : isPink ? (purple ? '#F6F3FA' : '#FBEFF5') : isWarm ? '#FAF7F2' : '#EEF2FF';
        const goal = inv.goal || 0;
        const currentBalance = getInvestmentBalance(inv.id);
        const calculatedBalance = getCalculatedInvestmentBalance(inv.id);
        const isOverridden = hasInvestmentOverride(inv.id);
        const projectedAmount = getProjectedInvestmentAmount(inv.id);
        const progressPercent = goal > 0 ? Math.min((currentBalance / goal) * 100, 100) : 0;
        const projectedPercent = goal > 0 ? Math.min((projectedAmount / goal) * 100, 100) : 0;
        const amountParts = splitCurrencyParts(formatCurrency(currentBalance));
        const breakdown = getInvestmentBreakdown(inv.id);
        const invTransactions = getInvestmentTransactions(inv.id);
        const isEditingBalance = editingInvestmentBalance === inv.id;

        return (
          <div ref={invPageRef} data-swipe-layer="1" className="fixed inset-0 z-[150] overflow-y-auto wy-plan-detail" style={{ backgroundColor: pageBg }}>
            <div className="max-w-4xl mx-auto px-4 pb-32" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}>

              <div className="flex items-center gap-3 mb-1">
                <button
                  onClick={() => { setDetailInvestmentId(null); setEditingInvestmentBalance(null); setOverrideBalanceInput(''); }}
                  className="w-9 h-9 rounded-full bg-white/75 flex items-center justify-center flex-shrink-0"
                  aria-label="Back to investments"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-800" />
                </button>
                <h2 className="text-[15px] text-gray-800 truncate" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {inv.name}
                </h2>
              </div>

              {/* Hero */}
              <div className="rounded-2xl p-4 mt-3 wy-hero wy-plan-dhero" style={{ backgroundColor: tint.wash }}>
                {plan ? planDetailTop({
                  tint,
                  emoji: '\u{1F4C8}',
                  status: isOverridden ? 'Manual · ' + manualUpdatedLabel(investManual(inv.id)) : 'Calculated', // dark_13
                  amountText: formatCurrency(currentBalance),
                  current: currentBalance,
                  goal,
                  pct: progressPercent,
                  projPct: projectedPercent,
                  tiles: [
                    ['Target', goal > 0 ? formatCurrency(goal) : 'None yet'],
                    ['Projected', `${projectedPercent.toFixed(0)}%`],
                    ['Calculated', formatCurrency(calculatedBalance)],
                  ],
                }) : (<>
                <h3 className="text-[14px]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1F2937' }}>
                  {inv.name}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.05em] mt-1 mb-3" style={{ color: tint.ink, opacity: 0.65, fontWeight: 700 }}>
                  {isOverridden ? 'Manual balance' : 'Calculated balance'} {'\u00B7'} {viewMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </p>

                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: '38px', lineHeight: 1, color: purple ? '#5E35A8' : tint.ink }}>
                  {amountParts.whole}<span style={{ fontSize: '20px' }}>{amountParts.cents}</span>
                </div>

                <p className="text-[12.5px] mt-1" style={{ color: tint.ink, opacity: 0.78 }}>
                  {goal > 0 ? `${progressPercent.toFixed(0)}% of ${formatCurrency(goal)}` : 'No target set'}
                </p>

                <div className="flex items-center gap-2.5 mt-4">
                  <span className="text-[10.5px] uppercase tracking-[0.04em] flex-shrink-0" style={{ color: tint.ink, opacity: 0.7, width: '52px', fontWeight: 700 }}>Now</span>
                  <div className="relative h-1.5 rounded-full overflow-hidden flex-1" style={{ backgroundColor: 'rgba(255,255,255,0.75)' }}>
                    <div className="absolute h-full rounded-full" style={{ width: `${progressPercent}%`, backgroundColor: tint.bar || tint.ink }} />
                  </div>
                  <span className="text-[11.5px] text-right flex-shrink-0" style={{ color: tint.ink, width: '82px', fontWeight: 500 }}>{formatCurrency(currentBalance)}</span>
                </div>
                <div className="flex items-center gap-2.5 mt-2">
                  <span className="text-[10.5px] uppercase tracking-[0.04em] flex-shrink-0" style={{ color: tint.ink, opacity: 0.55, width: '52px', fontWeight: 700 }}>Planned</span>
                  <div className="relative h-1.5 rounded-full overflow-hidden flex-1" style={{ backgroundColor: 'rgba(255,255,255,0.75)' }}>
                    <div className="absolute h-full rounded-full" style={{ width: `${projectedPercent}%`, backgroundColor: tint.mid }} />
                  </div>
                  <span className="text-[11.5px] text-right flex-shrink-0" style={{ color: tint.ink, opacity: 0.7, width: '82px', fontWeight: 500 }}>{formatCurrency(projectedAmount)}</span>
                </div>
                </>)}

                {isEditingBalance ? (
                  <div className="mt-4 rounded-xl p-3" style={{ backgroundColor: 'rgba(255,255,255,0.8)' }}>
                    <p className="text-[12.5px] mb-2" style={{ color: tint.ink, fontWeight: 500 }}>
                      Balance for {viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={overrideBalanceInput}
                        onChange={(e) => setOverrideBalanceInput(e.target.value)}
                        placeholder="Current balance"
                        className="pl-8 border-gray-200 bg-white"
                        autoFocus
                      />
                    </div>
                    <p className="text-[12px] text-gray-400 mt-2">Calculated balance is {formatCurrency(calculatedBalance)}</p>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => setInvestmentOverride(inv.id)} style={{ backgroundColor: plan ? '#1F1E24' : tint.ink }} className="hover:opacity-90 text-white" size="sm">
                        <Check className="w-4 h-4 mr-1" /> Save
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setEditingInvestmentBalance(null); setOverrideBalanceInput(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditingInvestmentBalance(inv.id);
                        setOverrideBalanceInput(currentBalance.toString());
                      }}
                      className="w-full mt-4 rounded-full py-2.5 text-[13.5px] text-white"
                      style={{ backgroundColor: plan ? '#1F1E24' : tint.ink, fontWeight: plan ? 600 : 500 }}
                    >
                      {isOverridden ? 'Update manual balance' : 'Enter balance manually'}
                    </button>
                    {hasManualThisMonth(inv.id) && ( /* dark_14: only when there's one to remove */
                      <button
                        onClick={() => clearInvestmentOverride(inv.id)}
                        className="w-full mt-2 rounded-full py-2.5 text-[13.5px] bg-white"
                        style={{ color: tint.ink, fontWeight: 500 }}
                      >
                        Use calculated ({formatCurrency(calculatedBalance)})
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Goal */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Goal</p>
              <div className="bg-white rounded-2xl shadow-sm px-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Target</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{goal > 0 ? formatCurrency(goal) : 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Projected</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{projectedPercent.toFixed(0)}% {'\u00B7'} {formatCurrency(projectedAmount)}</span>
                </div>
                <button
                  onClick={() => { setEditingInvestment(inv.id); setInvestmentGoalInput(goal.toString()); }}
                  className="w-full flex items-center justify-between py-3.5 text-[13.5px]"
                  style={{ color: tint.ink, fontWeight: 500 }}
                >
                  <span>Edit goal</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11.5px] text-gray-400 mt-2 px-1">Contributions are set up in the Budget tab.</p>

              {/* Balance */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Balance</p>
              <div className="bg-white rounded-2xl shadow-sm px-4 py-1">
                <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Starting balance</span>
                  <span className="text-[13.5px] text-gray-700">{formatCurrency(breakdown.startingBalance)}</span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                  <span className="text-[13.5px] text-gray-500">Deposits</span>
                  <span className="text-[13.5px] text-emerald-600">{formatCurrency(breakdown.deposits)}</span>
                </div>
                {breakdown.interest > 0 && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-[13.5px] text-gray-500">Growth</span>
                    <span className="text-[13.5px] text-[#5B8DB8]">{formatCurrency(breakdown.interest)}</span>
                  </div>
                )}
                {breakdown.withdrawals > 0 && (
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-[13.5px] text-gray-500">Withdrawals</span>
                    <span className="text-[13.5px] text-red-600">{formatCurrency(breakdown.withdrawals)}</span>
                  </div>
                )}
                <div className={`flex items-center justify-between py-2.5 ${isOverridden ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-[13.5px] text-gray-700" style={{ fontWeight: isOverridden ? 400 : 500 }}>Calculated balance</span>
                  <span className="text-[13.5px] text-gray-800" style={{ fontWeight: isOverridden ? 400 : 500 }}>{formatCurrency(calculatedBalance)}</span>
                </div>
                {isOverridden && (() => { /* dark_13: the manual balance, then what's gone in or out since */
                  const m = investManual(inv.id);
                  const since = changeSinceManual(inv, transactions, monthKey, { cutoffDay: localDay(viewMonthCutoff) });
                  return (
                    <>
                      <div className={`flex items-center justify-between py-2.5 ${Math.abs(since) >= 0.005 ? 'border-b border-gray-100' : ''}`}>
                        <span className="text-[13.5px] text-gray-700" style={{ fontWeight: 500 }}>Manual balance {'\u00B7'} {manualUpdatedLabel(m)}</span>
                        <span className="text-[13.5px] text-gray-800" style={{ fontWeight: 500 }}>{formatCurrency(m ? m.value : currentBalance)}</span>
                      </div>
                      {Math.abs(since) >= 0.005 && (
                        <div className="flex items-center justify-between py-2.5">
                          <span className="text-[13.5px] text-gray-500">{since > 0 ? 'Added since then' : 'Taken out since then'}</span>
                          <span className="text-[13.5px]" style={{ color: since > 0 ? '#2F8756' : '#C04B7C', fontWeight: 500 }}>{since > 0 ? '+' : '−'}{formatCurrency(Math.abs(since))}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Transactions */}
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mt-6 mb-2" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>Transactions</p>
              <div className="bg-white rounded-2xl shadow-sm px-4 py-1">
                {invTransactions.length > 0 ? (
                  invTransactions.map(t => (
                    <div key={t.id} className="py-2.5 border-b border-gray-100 last:border-0 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[14px] text-gray-800 truncate">{t.description}</p>
                        <p className="text-[12px] text-gray-400">
                          {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {t.is_interest && <span className="ml-1 text-blue-500">{'\u2022'} Growth</span>}
                        </p>
                      </div>
                      <span className={`text-[14px] font-medium flex-shrink-0 ${t.transaction_type === 'withdrawal' ? 'text-red-500' : 'text-green-600'}`}>
                        {t.transaction_type === 'withdrawal' ? '-' : '+'}{formatCurrency(t.amount)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-[13px] text-gray-400 py-4 text-center">No transactions yet.</p>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* Edit Investment Goal Overlay */}
      {editingInvestment !== null && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" onClick={() => { setEditingInvestment(null); setInvestmentGoalInput(''); }}>
          <div className="bg-white rounded-3xl w-full max-w-xs mx-4 overflow-hidden shadow-2xl wy-plan-goalpop" onClick={e => e.stopPropagation()}>
            <div className="p-5 text-center">
              <h3 className="text-lg font-bold text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Edit Goal 🎯
              </h3>
              <p className="text-[13px] text-gray-400 mt-1">What are you investing toward?</p>
            </div>
            <div className="px-5 pb-2">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={investmentGoalInput}
                  onChange={(e) => setInvestmentGoalInput(e.target.value)}
                  placeholder="Goal amount"
                  className="w-full h-14 pl-10 pr-4 text-center text-2xl font-bold rounded-xl focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-2 p-5 pt-3">
              <button onClick={() => { setEditingInvestment(null); setInvestmentGoalInput(''); }} className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-[15px] hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => editingInvestment && updateInvestment(editingInvestment)}
                className="flex-1 py-2.5 rounded-xl text-white font-semibold text-[15px]"
                style={{ background: theme === 'pink'
                  ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)'
                  : theme === 'warm'
                    ? '#5a9e78'
                    : '#6381f0' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SinkingFundsTab;
